import "../lib/readable_stream_polyfill"
import { clearAiDeepLink } from "../lib/ai_deep_link"
import { Controller } from "@hotwired/stimulus"

import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs"
import { extractPageHeaders, splitDrawingBySheets, splitSpecBySections } from "../pdf_splitter"
import { track } from "../lib/mixpanel"
import { showSheetPreview, hideSheetPreview } from "../lib/sheet_preview"
import { issueTagChip, guessIssueFromName } from "../lib/issue_tag"

// workerSrc is set lazily in connect() from the Stimulus value

const ALLOWED_EXTENSIONS = ["jpg","jpeg","gif","png","dng","heic","heif","webp","tiff","bmp","doc","docx","pdf","xml","txt","xls","xlsx","xlsm","zip","rar","dwg","pptx","ppt"]
const MAX_UPLOAD_SIZE = 250 * 1024 * 1024

const DISCIPLINE_PREFIXES = {
  "FP": "Fire Protection",
  "FA": "Fire Protection",
  "FS": "Fire Protection",
  "SK": "Sketch",
  "SD": "Civil",
  "EC": "Civil",
  "SW": "Civil",
  "UT": "Civil",
  "GR": "Civil",
  "ID": "Architectural",
  "A":  "Architectural",
  "S":  "Structural",
  "T":  "Structural",
  "M":  "Mechanical",
  "E":  "Electrical",
  "P":  "Plumbing",
  "C":  "Civil",
  "L":  "Landscape",
  "G":  "General"
}

const DISCIPLINE_COLORS = {
  "Architectural": "bg-blue-100 text-blue-700",
  "Structural":    "bg-amber-100 text-amber-700",
  "Mechanical":    "bg-purple-100 text-purple-700",
  "Electrical":    "bg-yellow-100 text-yellow-700",
  "Plumbing":      "bg-cyan-100 text-cyan-700",
  "Fire Protection":"bg-red-100 text-red-700",
  "Civil":         "bg-green-100 text-green-700",
  "Landscape":     "bg-lime-100 text-lime-700",
  "General":       "bg-gray-100 text-gray-600",
  "Sketch":        "bg-violet-100 text-violet-700"
}

// Thumbnails view (S2-14): each discipline's group of cards sits in a box
// tinted like its pill (Phelipe 2026-10-23). Full class strings so Tailwind
// finds them.
const DISCIPLINE_BOX = {
  "Architectural": "border-blue-200 bg-blue-50",
  "Structural":    "border-amber-200 bg-amber-50",
  "Mechanical":    "border-purple-200 bg-purple-50",
  "Electrical":    "border-yellow-200 bg-yellow-50",
  "Plumbing":      "border-cyan-200 bg-cyan-50",
  "Fire Protection":"border-red-200 bg-red-50",
  "Civil":         "border-green-200 bg-green-50",
  "Landscape":     "border-lime-200 bg-lime-50",
  "General":       "border-gray-200 bg-gray-50",
  "Sketch":        "border-violet-200 bg-violet-50",
  "Combined":      "border-blue-200 bg-blue-50"
}

// Conventional construction sheet order (General/site first, then structure,
// architecture, MEP) — used to order the discipline group rows.
const DISCIPLINE_ORDER = ["General", "Civil", "Landscape", "Structural", "Architectural", "Mechanical", "Plumbing", "Electrical", "Fire Protection", "Sketch"]

const EMPTY_RESULT = { sheet_number: null, title: null, discipline: null }

// Extract text content from first N pages of a PDF using pdfjs
async function extractPdfText(arrayBuffer, maxPages = 5) {
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  const pageCount = Math.min(pdf.numPages, maxPages)
  const pageTexts = []

  for (let i = 1; i <= pageCount; i++) {
    const page = await pdf.getPage(i)
    const content = await page.getTextContent()
    const text = content.items.map(item => item.str).join(" ")
    pageTexts.push(text)
  }

  return { pageTexts, totalPages: pdf.numPages }
}

// Render first N pages as JPEG images (fallback for scanned PDFs with no text)
async function renderPdfPagesToJpegs(arrayBuffer, maxPages = 5, targetWidth = 800) {
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  const pageCount = Math.min(pdf.numPages, maxPages)
  const images = await Promise.all(
    Array.from({ length: pageCount }, async (_, i) => {
      const page = await pdf.getPage(i + 1)
      const viewport = page.getViewport({ scale: 1 })
      const scale = targetWidth / viewport.width
      const scaledViewport = page.getViewport({ scale })
      const canvas = document.createElement("canvas")
      canvas.width = scaledViewport.width
      canvas.height = scaledViewport.height
      const ctx = canvas.getContext("2d")
      ctx.fillStyle = "#fff"
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise
      const jpegBase64 = canvas.toDataURL("image/jpeg", 0.70).split(",")[1]
      canvas.width = canvas.height = 0
      return jpegBase64
    })
  )
  return { images, totalPages: pdf.numPages }
}

async function callExtractMetadata(url, mode, content, filename) {
  const token = document.querySelector('meta[name="csrf-token"]')?.content
  const body = { mode, filename }
  if (mode === "text") body.text = content
  else body.images = content

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    credentials: "same-origin",
    signal: AbortSignal.timeout(35000),
    body: JSON.stringify(body)
  })
  if (!res.ok) throw new Error(`extract_metadata HTTP ${res.status}`)
  return await res.json()
}

function detectDiscipline(sheetNumber) {
  if (!sheetNumber) return null
  const prefix = sheetNumber.trim().toUpperCase().replace(/[-\s].*/, "")
  return DISCIPLINE_PREFIXES[prefix.substring(0, 2)] || DISCIPLINE_PREFIXES[prefix.substring(0, 1)] || null
}

export default class extends Controller {
  static targets = [
    "viewerPrevBtn", "viewerNextBtn",
    "tableContainer", "tableBody", "gridContainer", "sentinel", "empty", "skeleton",
    "searchInput", "searchClear", "aiFilterChip", "filterBadge", "clearFiltersRow", "datePresets", "dateFrom", "dateTo",
    "viewSwitcher", "disciplineTabs", "disciplineLabel", "disciplineMenu", "showAllRevisionsToggle",
    "selectAllCheckbox", "selectAllCol", "bulkToggle",
    "detailModal", "detailBackdrop", "detailPanel", "detailContent", "detailLoading",
    "detailTitle", "detailPreview", "detailMeta", "detailDownloadBtn", "prevBtn", "nextBtn",
    "detailRevisionHistory", "detailLinkedItems",
    "uploadModal", "dropZone", "fileInput", "uploadSubmitBtn", "uploadMenu", "uploadMenuBtn",
    "uploadSetModeSelect",
    "uploadNewSetFields", "uploadExistingSetFields",
    "uploadSetNameInput", "uploadSetDateInput", "uploadSetSelect", "uploadSetTypeSelect", "uploadSetNumberInput",
    "uploadSheetTable", "uploadSheetRows", "uploadAiStatus", "uploadAiStatusText",
    "bulkBar", "bulkCount", "compareBtn", "compareChoiceModal", "compareChoiceOverlayBtn",
    "comparePickerModal", "comparePickerTitle", "comparePickerList", "comparePickerEmpty",
    "comparePickerPreviousWrap", "comparePickerOlderSection",
    "deleteModal", "deleteMessage", "wrongTypeModal", "wrongTypeLink",
    "moveModal", "moveMessage",
    "editModal", "editSheetInput", "editTitleInput", "editDisciplineSelect", "editSetSelect", "editReceivedInput",
    "editSetTypeSelect", "editSetNumberInput",
    "rowMenu",
    "drawingSetFilter",
    "exportModal", "exportDisciplines", "exportSetSelect", "exportHistorical", "exportFileCount", "exportSubmitBtn", "exportCompiledBtn",
    "exportStaleWarning", "exportStaleText",
    "distributeModal", "distributeSearchInput", "distributeDropdownList", "distributeSendBtn",
    "distributeSelectAll", "distributeCount",
    "sortIconSheet", "sortIconTitle", "sortIconDiscipline", "sortIconRevision", "sortIconSet", "sortIconReceived",
    "bluebeamPopover", "bluebeamCopyLinkLabel", "bluebeamSessionId", "bluebeamSessionIdCopyLabel",
    "bluebeamFinishModal", "bluebeamVersionRadio",
    "bluebeamFinishNextRev", "bluebeamFinishCurrentRev", "bluebeamDiscardModal"
  ]

  static values = {
    projectId: Number,
    filesUrl: String,
    presignUrl: String,
    registerUrl: String,
    checkSheetsUrl: String,
    checkFilesUrl: String,
    extractMetadataUrl: String,
    analyzeCombinedUrl: String,
    specificationsPageUrl: String,
    specsRegisterUrl: String,
    specsCheckSectionsUrl: String,
    pdfWorkerUrl: String,
    drawingSetsUrl: String,
    saveViewModeUrl: String,
    sheetThumbsUrl: String,
    saveFiltersUrl: String,
    distributeUrl: String,
    distributionMembersUrl: String,
    exportZipUrl: String,
    exportCompiledPdfUrl: String,
    compiledPdfStatusUrl: String,
    exportCountUrl: String,
    sheetHistoryUrlTemplate: String,
    savedFilters: { type: Object, default: {} },
    currentView: { type: String, default: "list" },
    canManageDocuments: { type: Boolean, default: false },
    bluebeamAllowed: { type: Boolean, default: false },
    builder: { type: Boolean, default: false },
    pdfViewerBaseUrl: String,
    splitDocumentsUrl: String,
    referencesUrl: String,
    bluebeamOpenUrlTemplate: String,
    bluebeamFinishUrlTemplate: String,
    bluebeamCloseUrlTemplate: String,
    bluebeamStatusUrlTemplate: String,
    copyMarkupsLabel: { type: String, default: "Copy the previous version's markups to this version" }
  }

  connect() {
    if (this.pdfWorkerUrlValue) pdfjsLib.GlobalWorkerOptions.workerSrc = this.pdfWorkerUrlValue
    this._page = 1
    this._loading = false
    this._isLastPage = false
    this._searchTimeout = null
    this._filters = {}
    this._fileIds = []
    this._filesData = {}
    this._activeFileId = null
    this._modalOpen = false
    this._selectedIds = new Set()
    this._bulkMode = false
    this._uploadFiles = []
    this._activeDiscipline = null
    this._statusFilter = "current"
    this._drawingSetFilter = null
    this._deleteTarget = null
    this._rowIndex = 0
    this._sort = { key: null, order: null }
    this._onKeydown = this._onKeydown.bind(this)
    this._drawingSetsCache = []
    this._bluebeamPollers = {}

    // "Markup in Bluebeam" button inside the PDF viewer
    this._onOpenBluebeam = (e) => {
      const id = e.detail?.pdfId
      if (id) this._triggerBluebeamOpen(id)
    }
    this.element.addEventListener("pdf-viewer:open-bluebeam", this._onOpenBluebeam)

    // Returning from the Bluebeam OAuth dance: a session is being created
    // for the doc in ?bluebeam_poll_doc — poll it and clean the URL.
    const bbPollDoc = new URLSearchParams(window.location.search).get("bluebeam_poll_doc")
    if (bbPollDoc) {
      this._pollBluebeamStatus(bbPollDoc)
      const cleanUrl = new URL(window.location.href)
      cleanUrl.searchParams.delete("bluebeam_poll_doc")
      window.history.replaceState({}, "", cleanUrl)
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this._loading && !this._isLastPage) {
          this._loadPage(this._page)
        }
      },
      { rootMargin: "300px", threshold: 0 }
    )

    // AI Radar deep link: ?ai_open=<id>&ai_page=<n>&ai_q=<sheet #> — filter
    // the list to the cited sheet and open it in the viewer at that page.
    // ai_hl is a verbatim quote to search & highlight inside the PDF (falls
    // back to the sheet number); ai_return is the action-item path to go
    // back to when the viewer is closed.
    const aiParams = new URLSearchParams(window.location.search)
    this._aiOpenId = aiParams.get("ai_open")
    this._aiOpenPage = parseInt(aiParams.get("ai_page"), 10) || 1
    const aiQ = aiParams.get("ai_q")
    this._aiOpenHl = (aiParams.get("ai_hl") || aiQ || "").trim() || null
    // ai_locate=<id>: locate-only deep link from ChatLog's Ask AI sources
    // (Phelipe 2026-08-24) — filter the list to the cited document and pulse
    // the row — without opening the PDF viewer.
    const aiLocate = aiParams.get("ai_locate")
    if (aiLocate && !this._aiOpenId) {
      this._aiLocateId = aiLocate
      this._aiSources = [{ id: aiLocate }]
    }
    const aiReturn = aiParams.get("ai_return")
    this._aiReturnUrl = (aiReturn && aiReturn.startsWith("/") && !aiReturn.startsWith("//")) ? aiReturn : null
    if (this._aiReturnUrl) {
      this._onPdfClosedReturn = () => {
        if (!this._aiReturnUrl) return
        const url = this._aiReturnUrl
        this._aiReturnUrl = null
        window.location.assign(url)
      }
      this.element.addEventListener("pdf-viewer:closed", this._onPdfClosedReturn)
    }
    // Read once, then out of the address — or Close reopened the sheet (Larry K7-25).
    clearAiDeepLink()

    // The Thumbnails view's per-row highlight boxes follow the cards when
    // the window changes width.
    this._onGridResize = () => {
      clearTimeout(this._gridResizeTimer)
      this._gridResizeTimer = setTimeout(() => this._renderGrid(), 120)
    }
    window.addEventListener("resize", this._onGridResize)

    this._restoreSavedFilters()
    if (aiQ) {
      this._filters.search = aiQ
      if (this.hasSearchInputTarget) this.searchInputTarget.value = aiQ
    }
    this._loadPage(1)

    if (this.canManageDocumentsValue) {
      this._setupPageDrag()
    }

    // "Show me" from the Ask-AI panel → filter the list to the answer's sources.
    this._onAiSources = (e) => this._applyAiSources(e.detail?.sources)
    window.addEventListener("docs-ai:show-sources", this._onAiSources)
  }

  disconnect() {
    if (this._observer) this._observer.disconnect()
    this._stopThumbPoll()
    document.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("docs-ai:show-sources", this._onAiSources)
    window.removeEventListener("resize", this._onGridResize)
    this._teardownPageDrag()
    this.element.removeEventListener("pdf-viewer:open-bluebeam", this._onOpenBluebeam)
    if (this._onPdfClosedReturn) this.element.removeEventListener("pdf-viewer:closed", this._onPdfClosedReturn)
    Object.values(this._bluebeamPollers || {}).forEach(id => clearInterval(id))
    this._bluebeamPollers = {}
  }

  // ── Column Sorting ──────────────────────────────────────────────

  sortColumn(event) {
    const key = event.currentTarget.dataset.sortKey
    if (this._sort.key === key) {
      this._sort.order = this._sort.order === "asc" ? "desc" : "asc"
    } else {
      this._sort.key = key
      this._sort.order = "asc"
    }
    this._reRenderTable()
  }

  _reRenderTable() {
    this._rowIndex = 0
    let files = this._fileIds.map(id => this._filesData[id]).filter(Boolean)
    // Historical view shows only drawings that HAVE history (Larry
    // 2026-08-18): a sheet with a single revision has nothing historical to
    // look at, so it drops out instead of burying the versioned ones.
    if (this._statusFilter === "all") {
      const counts = {}
      files.forEach(f => { const k = (f.sheet_number || "").trim() || `id-${f.id}`; counts[k] = (counts[k] || 0) + 1 })
      files = files.filter(f => counts[(f.sheet_number || "").trim() || `id-${f.id}`] > 1)
    }
    const sorted = this._sortFiles(files)
    this.tableBodyTarget.innerHTML = this._renderRowsTree(sorted)
    this._updateSortIcons()
    // Navigation order = what's on screen, top to bottom (Larry 2026-08-18:
    // "right = the sheet below, left = the one above"). The fetch order in
    // _fileIds doesn't match the grouped/sorted render, which is why the
    // arrows walked the list backwards (and skipped) before.
    // Group-header rows carry synthetic ids (discgrp-…) — only real numeric
    // document ids belong in the arrows' walk (prod 500 on
    // /drawings/discgrp-top-0/pdf_viewer_data, 2026-08-20).
    this._navIds = [...new Set([...this.tableBodyTarget.querySelectorAll("tr[data-file-id]")].map(r => r.dataset.fileId))].filter(id => /^\d+$/.test(id))
    // Rows just got replaced — a hover card anchored to an old row would
    // never receive its mouseleave.
    hideSheetPreview()
  }

  _navOrder() {
    return (this._navIds && this._navIds.length) ? this._navIds : this._fileIds
  }

  _sortFiles(items) {
    // Builder: the combined set original is always pinned on top, with
    // its extracted sheets below it.
    const combinedFirst = (a, b) => (a.combined === b.combined ? 0 : a.combined ? -1 : 1)
    if (!this._sort.key) return [...items].sort(combinedFirst)
    const sorted = [...items]
    const dir = this._sort.order === "desc" ? -1 : 1
    sorted.sort((a, b) => {
      const cf = combinedFirst(a, b)
      if (cf !== 0) return cf
      let va, vb
      switch (this._sort.key) {
        case "sheet":
          // Natural compare: 201 < 202 < 1001 < 2010 (string compare would
          // wedge 2010 between 201 and 202).
          return (a.sheet_number || "").localeCompare(b.sheet_number || "", undefined, { numeric: true, sensitivity: "base" }) * dir
        case "title":
          va = (a.title || "").toLowerCase(); vb = (b.title || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "discipline":
          va = (a.discipline || "").toLowerCase(); vb = (b.discipline || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "revision":
          va = parseInt(a.revision_number) || 0; vb = parseInt(b.revision_number) || 0
          return (va - vb) * dir
        case "set":
          va = (a.drawing_set_name || "").toLowerCase(); vb = (b.drawing_set_name || "").toLowerCase()
          return va < vb ? -dir : va > vb ? dir : 0
        case "received":
          va = a.received_date || a.formatted_date || ""
          vb = b.received_date || b.formatted_date || ""
          return va < vb ? -dir : va > vb ? dir : 0
        default: return 0
      }
    })
    return sorted
  }

  _updateSortIcons() {
    const arrowUp = `<svg class="w-3.5 h-3.5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5"/></svg>`
    const arrowDown = `<svg class="w-3.5 h-3.5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg>`
    const keys = { sheet: "Sheet", title: "Title", discipline: "Discipline", revision: "Revision", set: "Set", received: "Received" }
    for (const [key, name] of Object.entries(keys)) {
      const targetName = `sortIcon${name}Target`
      const hasName = `hasSortIcon${name}Target`
      if (this[hasName]) {
        this[targetName].innerHTML = this._sort.key === key ? (this._sort.order === "asc" ? arrowUp : arrowDown) : ""
      }
    }
  }

  // ── Search (300ms debounce) ────────────────────────────────────────

  search() {
    if (this._aiSources) this._clearAiState() // typing replaces the AI filter
    this._toggleSearchClear()
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => {
      this._filters.search = this.searchInputTarget.value.trim()
      this._resetAndLoad()
      this._persistFilters()
    }, 300)
  }

  // "×" inside the search box — wipes the term and reloads the list.
  clearSearch() {
    clearTimeout(this._searchTimeout)
    this.searchInputTarget.value = ""
    this._filters.search = ""
    this._toggleSearchClear()
    this._resetAndLoad()
    this._persistFilters()
    this.searchInputTarget.focus()
  }

  // The × is only visible while there's something to clear.
  _toggleSearchClear() {
    if (this.hasSearchClearTarget) this.searchClearTarget.classList.toggle("hidden", !this.searchInputTarget.value)
  }

  // ── "Show me" (AI answer filter) ───────────────────────────────────
  // Filters the list to the documents an Ask-AI answer drew from, with the
  // answer's quotes as the expandable per-row snippets.

  _applyAiSources(sources) {
    const valid = (Array.isArray(sources) ? sources : []).filter(s => s && s.id)
    if (!valid.length) return
    this._aiSources = valid
    // The AI filter replaces any typed search — clear it so they don't fight.
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    this._filters.search = ""
    this._toggleSearchClear()
    this._updateAiChip()
    this._resetAndLoad()
    track("AI Show Me Clicked", {
      project_id: this.projectIdValue,
      module: "drawings",
      documents: new Set(valid.map(s => String(s.id))).size
    })
  }

  clearAiFilter(e) {
    if (e) e.preventDefault()
    if (!this._aiSources) return
    this._clearAiState()
    this._resetAndLoad()
  }

  _clearAiState() {
    this._aiSources = null
    this._updateAiChip()
  }

  _updateAiChip() {
    if (!this.hasAiFilterChipTarget) return
    const on = !!this._aiSources
    this.aiFilterChipTarget.classList.toggle("hidden", !on)
    this.aiFilterChipTarget.classList.toggle("inline-flex", on)
    if (on) {
      const n = new Set(this._aiSources.map(s => String(s.id))).size
      const label = this.aiFilterChipTarget.querySelector("[data-ai-chip-label]")
      if (label) label.textContent = `AI answer · ${n} doc${n === 1 ? "" : "s"}`
    }
  }

  // Synthesize the per-row badge + snippet data from the AI's cited quotes,
  // reusing the search-results rendering as-is.
  _mergeAiMatches() {
    const map = {}
    this._aiSources.forEach(s => {
      const k = String(s.id)
      map[k] ||= { count: 0, pages: [], more_pages: 0 }
      map[k].count++
      map[k].pages.push({ page: s.page || 1, count: 1, snippet: s.quote || s.label || "", term: s.quote || "" })
    })
    this._contentMatches = map
  }

  // ── Date Filters ──────────────────────────────────────────────────

  selectDatePreset(event) {
    const preset = event.currentTarget.dataset.preset
    const today = new Date()
    let dateFrom = ""

    if (preset) {
      const d = new Date(today)
      d.setDate(d.getDate() - parseInt(preset))
      dateFrom = d.toISOString().slice(0, 10)
    }

    this._filters.date_from = dateFrom
    this._filters.date_to = dateFrom ? today.toISOString().slice(0, 10) : ""

    if (this.hasDateFromTarget) this.dateFromTarget.value = this._filters.date_from
    if (this.hasDateToTarget) this.dateToTarget.value = this._filters.date_to

    this.datePresetsTargets.forEach(container => {
      container.querySelectorAll("button").forEach(btn => {
        if (btn.dataset.preset === preset) {
          btn.className = btn.className.replace(/text-gray-500 hover:bg-gray-50 border-gray-200/, "bg-brand/10 text-brand font-medium border-brand/30")
        } else {
          btn.className = btn.className.replace(/bg-brand\/10 text-brand font-medium border-brand\/30/, "text-gray-500 hover:bg-gray-50 border-gray-200")
        }
      })
    })

    this._resetAndLoad()
    this._persistFilters()
    this._updateFilterBadge()
  }

  filterByDate() {
    this._filters.date_from = this.hasDateFromTarget ? this.dateFromTarget.value : ""
    this._filters.date_to = this.hasDateToTarget ? this.dateToTarget.value : ""

    this.datePresetsTargets.forEach(container => {
      container.querySelectorAll("button").forEach(btn => {
        btn.className = btn.className.replace(/bg-brand\/10 text-brand font-medium border-brand\/30/, "text-gray-500 hover:bg-gray-50 border-gray-200")
      })
    })

    this._resetAndLoad()
    this._persistFilters()
    this._updateFilterBadge()
  }

  filterByDrawingSet() {
    this._drawingSetFilter = this.hasDrawingSetFilterTarget ? this.drawingSetFilterTarget.value || null : null
    this._resetAndLoad()
    this._updateFilterBadge()
  }

  selectDrawingSet(event) {
    event.preventDefault()
    const setId = event.currentTarget.dataset.setId || null
    this._drawingSetFilter = setId === "" ? null : setId
    this._populateDrawingSetFilter(this._drawingSetsCache || [])
    this._resetAndLoad()
    this._updateFilterBadge()
  }

  clearFilters() {
    this._filters = {}
    this._drawingSetFilter = null
    this._clearAiState()
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    this._toggleSearchClear()
    if (this.hasDateFromTarget) this.dateFromTarget.value = ""
    if (this.hasDateToTarget) this.dateToTarget.value = ""
    this._populateDrawingSetFilter(this._drawingSetsCache || [])

    this.datePresetsTargets.forEach(container => {
      const buttons = container.querySelectorAll("button")
      buttons.forEach((btn, i) => {
        if (i === 0) {
          btn.className = btn.className.replace(/text-gray-500 hover:bg-gray-50 border-gray-200/, "bg-brand/10 text-brand font-medium border-brand/30")
        } else {
          btn.className = btn.className.replace(/bg-brand\/10 text-brand font-medium border-brand\/30/, "text-gray-500 hover:bg-gray-50 border-gray-200")
        }
      })
    })

    this._resetAndLoad()
    this._persistFilters()
    this._updateFilterBadge()
  }

  // ── View Toggle ───────────────────────────────────────────────────

  switchView(event) {
    const view = event.currentTarget.dataset.view
    if (view === this.currentViewValue) return
    this.currentViewValue = view
    track("Drawings View Switched", { project_id: this.projectIdValue, view })

    this.viewSwitcherTarget.querySelectorAll("button").forEach(btn => {
      if (btn.dataset.view === view) {
        btn.classList.remove("text-text-muted", "font-medium", "hover:text-gray-700")
        btn.classList.add("text-brand", "font-bold")
      } else {
        btn.classList.remove("text-brand", "font-bold")
        btn.classList.add("text-text-muted", "font-medium", "hover:text-gray-700")
      }
    })

    if (view === "list") {
      this.tableContainerTarget.classList.remove("hidden")
      this.hasGridContainerTarget && this.gridContainerTarget.classList.add("hidden")
    } else {
      this.tableContainerTarget.classList.add("hidden")
      this.hasGridContainerTarget && this.gridContainerTarget.classList.remove("hidden")
      this.hasGridContainerTarget && this.gridContainerTarget.classList.add("flex")
    }

    this._resetAndLoad()

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.saveViewModeUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      credentials: "same-origin",
      body: JSON.stringify({ mode: view })
    })
  }

  // ── Discipline Tabs ─────────────────────────────────────────────

  selectDiscipline(event) {
    event.preventDefault()
    const disc = event.currentTarget.dataset.discipline || null
    this._activeDiscipline = disc === "" ? null : disc
    this._updateDisciplineTabStyles()
    this._updateDisciplineLabel()
    // Close the dropdown
    this.disciplineMenuTarget.classList.add("hidden")
    this._resetAndLoad()
  }

  // ── Show All Revisions Toggle ───────────────────────────────────

  toggleAllRevisions(event) {
    this._statusFilter = event.currentTarget.checked ? "all" : "current"
    this._resetAndLoad()
  }

  // ── Selection ─────────────────────────────────────────────────────

  toggleSelectAll(event) {
    const checked = event.currentTarget.checked
    const checkboxes = this.element.querySelectorAll('[data-file-checkbox]')
    checkboxes.forEach(cb => {
      cb.checked = checked
      const id = cb.dataset.fileId
      if (checked) this._selectedIds.add(id)
      else this._selectedIds.delete(id)
    })
    this._updateBulkBar()
  }

  toggleFileSelect(event) {
    const id = event.currentTarget.dataset.fileId
    if (event.currentTarget.checked) {
      this._selectedIds.add(id)
    } else {
      this._selectedIds.delete(id)
    }
    if (this.hasSelectAllCheckboxTarget) {
      const total = this.element.querySelectorAll('[data-file-checkbox]').length
      this.selectAllCheckboxTarget.checked = this._selectedIds.size === total
      this.selectAllCheckboxTarget.indeterminate = this._selectedIds.size > 0 && this._selectedIds.size < total
    }
    this._updateBulkBar()
  }

  toggleBulkMode() {
    this._bulkMode = !this._bulkMode
    this._selectedIds.clear()

    // Toggle Select button vs bulk bar
    if (this.hasBulkToggleTarget) this.bulkToggleTarget.classList.toggle("hidden", this._bulkMode)
    if (this.hasBulkBarTarget) {
      this.bulkBarTarget.classList.toggle("hidden", !this._bulkMode)
      if (this._bulkMode) this.bulkBarTarget.classList.add("flex")
      else this.bulkBarTarget.classList.remove("flex")
    }

    // Toggle header checkbox column
    if (this.hasSelectAllColTarget) this.selectAllColTarget.classList.toggle("hidden", !this._bulkMode)
    if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.checked = false
      this.selectAllCheckboxTarget.indeterminate = false
    }

    // Keep column widths aligned: the table is fixed-layout sized by the
    // colgroup, so give the checkbox its own narrow column while bulk mode
    // is on (otherwise every column shifts and Sheet # goes extra-wide).
    const cg = this.hasTableContainerTarget ? this.tableContainerTarget.querySelector("colgroup") : null
    if (cg) {
      let cbCol = cg.querySelector("[data-cb-col]")
      if (this._bulkMode && !cbCol) {
        cbCol = document.createElement("col")
        cbCol.setAttribute("data-cb-col", "")
        cbCol.style.width = "40px"
        cg.insertBefore(cbCol, cg.children[1] || null)
      } else if (!this._bulkMode && cbCol) {
        cbCol.remove()
      }
    }

    // Toggle row checkboxes
    // Rows in the list and cards in the Thumbnails view alike.
    this.element.querySelectorAll("[data-checkbox-col]").forEach(td => {
      td.classList.toggle("hidden", !this._bulkMode)
    })
    this.element.querySelectorAll("[data-file-checkbox]").forEach(cb => {
      cb.checked = false
    })

    this._updateBulkBar()
  }

  deselectAll() {
    this._selectedIds.clear()
    this.element.querySelectorAll('[data-file-checkbox]').forEach(cb => cb.checked = false)
    if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.checked = false
      this.selectAllCheckboxTarget.indeterminate = false
    }
    this._updateBulkBar()
  }

  // ── Bulk Actions ──────────────────────────────────────────────────

  bulkDelete() {
    if (this._selectedIds.size === 0) return
    this._deleteTarget = { type: "bulk" }
    this.deleteMessageTarget.textContent = `Are you sure you want to delete ${this._selectedIds.size} file(s)?`
    this.deleteModalTarget.classList.remove("hidden")
  }

  // ── Detail Slide-Over ─────────────────────────────────────────────

  openFile(event) {
    if (event.target.closest('[data-file-checkbox]') || event.target.closest('[data-action-menu]')) return
    const id = event.currentTarget.dataset.fileId
    if (!id) return

    // Search-inside-documents: when this row matched the document's text, a
    // row click expands/collapses its snippet panel instead of opening the
    // file (the panel's items open the file at the matched page).
    if (event.currentTarget.tagName === "TR" && this._contentMatches?.[String(id)]) {
      this._toggleMatchRow(event.currentTarget, String(id))
      return
    }

    // Open PDF files in Apryse viewer, fall back to detail drawer for non-PDFs
    const file = this._filesData?.[String(id)]
    const ext = file?.file_name?.split(".")?.pop()?.toLowerCase()

    if (ext === "pdf") {
      this._openInViewer(id)
    } else {
      this._openDetail(id)
    }
  }

  // Open a document in the Apryse viewer. `extra` lets search results land on
  // the matched page with the term pre-searched ({ initialPage, searchTerm }).
  _openInViewer(id, extra = {}) {
    const file = this._filesData?.[String(id)]
    if (!file) return
    const dataUrl = this.pdfViewerBaseUrlValue.replace(/\/0\//, `/${id}/`)
    const viewer = this.element.querySelector("[data-controller='action-item-pdf-viewer']")
    const viewerCtrl = viewer && this.application.getControllerForElementAndIdentifier(viewer, "action-item-pdf-viewer")
    if (!viewerCtrl) return

    const saveUrl = dataUrl.replace("pdf_viewer_data", "save_pdf_annotations")
    const label = [file.sheet_number, file.title].filter(Boolean).join(" — ")
    const displayName = label ? `${label}  ·  ${file.file_name}` : file.file_name
    // Allow comparing from inside the viewer — only for owners/consultants,
    // not the multi-page Combined file (semantic diff is per-sheet), and
    // only when an older version of this sheet exists to compare against
    // (a sheet at revision 2+ has at least one earlier version).
    const hasOlderVersion = parseInt(file.revision_number, 10) >= 2
    const compareEnabled = this.canManageDocumentsValue && file.is_pdf && !file.combined && hasOlderVersion
    const bluebeamEnabled = this.bluebeamAllowedValue && this.hasBluebeamOpenUrlTemplateValue && this._bluebeamEligible(file)
    // "Split" picker: all specs and all drawings — minus the one already
    // open. A drawing sheet has no ISPWC series, so no standard drawings
    // apply (the section_number won't match a series).
    const splitListUrl = this.hasSplitDocumentsUrlValue
      ? `${this.splitDocumentsUrlValue}?exclude=${id}&exclude_type=drawing&section_number=${encodeURIComponent(file.sheet_number || file.number || "")}`
      : null
    // References strip: documents THIS sheet cites, detected from its text.
    const referencesUrl = this.hasReferencesUrlValue
      ? `${this.referencesUrlValue}?doc_id=${id}&doc_type=drawing`
      : null
    // Prev/next sheets' data URLs — the viewer prefetches their PDFs after
    // this one loads so arrow navigation swaps near-instantly.
    const _nav = this._navOrder()
    const _nIdx = _nav.indexOf(String(id))
    const neighbors = [_nav[_nIdx + 1], _nav[_nIdx - 1]]
      .filter(Boolean)
      .map(nid => this.pdfViewerBaseUrlValue.replace(/\/0\//, `/${nid}/`))
    // Same two requests as always, just fired together: the list already
    // carries this sheet's direct URL, so the download starts NOW while the
    // metadata request runs in parallel (zero extra requests or cost).
    if (file.pdf_url && typeof viewerCtrl.primeBlob === "function") {
      viewerCtrl.primeBlob(dataUrl, file.pdf_url)
    }
    viewerCtrl.open({ dataUrl, filename: displayName, punchlistId: id, saveUrl, compareEnabled, compareFileId: id, bluebeamEnabled, version: file.combined ? null : file.revision_number, versionCurrent: file.status === "current", versionTag: file.combined ? null : file.issue_tag, versionTagTitle: file.drawing_set_name, splitListUrl, referencesUrl, neighbors, ...extra })
    // Remember which doc is open so the viewer Prev/Next can page the list.
    this._viewerFileId = String(id)
    const _vIdx = this._navOrder().indexOf(String(id))
    if (this.hasViewerPrevBtnTarget) this.viewerPrevBtnTarget.disabled = _vIdx <= 0
    if (this.hasViewerNextBtnTarget) this.viewerNextBtnTarget.disabled = _vIdx < 0 || _vIdx >= this._navOrder().length - 1
  }

  // ── Search-inside-documents results ───────────────────────────────

  // Snippet click → open the PDF at that page with the term pre-searched.
  // AI-filter snippets carry their own phrase (data-term, from the quote).
  openFileAtPage(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.fileId
    const page = parseInt(event.currentTarget.dataset.page, 10) || 1
    const term = event.currentTarget.dataset.term || this._filters.search || null
    this._openInViewer(id, { initialPage: page, searchTerm: term })
  }

  _toggleMatchRow(rowEl, id) {
    const existing = this.tableBodyTarget.querySelector(`[data-match-row-for="${id}"]`)
    const chev = rowEl.querySelector("[data-match-chevron]")
    if (existing) {
      existing.remove()
      chev?.classList.remove("rotate-180")
      return
    }
    const file = this._filesData?.[String(id)]
    const match = this._contentMatches?.[String(id)]
    if (!file || !match) return
    // Span only the VISIBLE columns — a display:none td (hidden checkbox col)
    // still counts as a child, and an oversized colspan makes the browser
    // reserve a phantom column of dead space on the right.
    const colCount = rowEl.querySelectorAll("td:not(.hidden)").length
    rowEl.insertAdjacentHTML("afterend", this._matchRowHtml(file, match, colCount))
    chev?.classList.add("rotate-180")
  }

  _matchRowHtml(file, match, colCount) {
    const term = this._filters.search || ""
    // AI-filter snippets open the viewer searching a short phrase from their
    // own quote (first 8 words) instead of the (empty) search-bar term.
    const termAttr = (p) => {
      if (!p.term) return ""
      const phrase = String(p.term).split(/\s+/).slice(0, 8).join(" ")
      return ` data-term="${this._escapeHtml(phrase).replace(/"/g, "&quot;")}"`
    }
    const items = (match.pages || []).map(p => `
      <button type="button" data-action="click->drawings-specs#openFileAtPage" data-file-id="${file.id}" data-page="${p.page}"${termAttr(p)}
              class="w-full flex items-start gap-2.5 text-left rounded-lg border border-gray-200 bg-white hover:border-brand/40 hover:bg-brand/[0.03] px-3 py-2 cursor-pointer">
        <span class="shrink-0 inline-flex items-center rounded bg-gray-100 text-gray-600 px-1.5 py-px text-[13px] font-medium mt-px">p. ${p.page}</span>
        <span class="text-[13px] text-gray-700 leading-5 min-w-0 break-words">${this._highlightTerm(p.snippet, term)}</span>
        ${p.count > 1 ? `<span class="ml-auto shrink-0 text-[13px] text-gray-400">${p.count}×</span>` : ""}
      </button>`).join("")
    const more = match.more_pages > 0
      ? `<div class="text-[13px] text-gray-400 mt-1.5">+ ${match.more_pages} more page${match.more_pages === 1 ? "" : "s"} with matches — open the document to see all</div>`
      : ""
    // The w-0/min-w-full wrapper keeps this wide content from contributing to
    // the table's auto layout — otherwise a long snippet stretches the table
    // and every other row shows dead space on the right.
    return `<tr data-match-row-for="${file.id}" class="border-b border-gray-200 bg-brand/[0.02]">
      <td colspan="${colCount}" class="px-4 xl:px-6 py-3">
        <div class="w-0 min-w-full">
          <div class="flex items-center justify-between gap-3 mb-2">
            <span class="text-[13px] font-medium text-gray-700">${match.count} result${match.count === 1 ? "" : "s"} inside this document</span>
            <button type="button" data-action="click->drawings-specs#openFileAtPage" data-file-id="${file.id}" data-page="${match.pages?.[0]?.page || 1}"
                    class="text-[13px] text-brand font-medium hover:underline cursor-pointer shrink-0">Open document →</button>
          </div>
          <div class="space-y-1.5">${items}</div>
          ${more}
        </div>
      </td>
    </tr>`
  }

  // Escape, then wrap each occurrence of the term in a highlight mark.
  _highlightTerm(text, term) {
    const esc = this._escapeHtml(text || "")
    if (!term) return esc
    const safe = this._escapeHtml(term).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    try {
      return esc.replace(new RegExp(safe, "gi"), m => `<mark class="bg-amber-200/80 text-gray-900 rounded-sm px-0.5">${m}</mark>`)
    } catch (_) {
      return esc
    }
  }

  closeDetail() {
    if (!this._modalOpen) return
    this._modalOpen = false
    this.detailBackdropTarget.classList.remove("opacity-100")
    this.detailBackdropTarget.classList.add("opacity-0")
    this.detailPanelTarget.classList.add("translate-x-full")
    setTimeout(() => {
      this.detailModalTarget.classList.add("hidden")
    }, 300)
    document.removeEventListener("keydown", this._onKeydown)
    this._activeFileId = null
  }

  backdropMousedown(event) {
    this._backdropMousedown = event.target === this.detailBackdropTarget
  }

  closeOnBackdrop(event) {
    if (this._backdropMousedown && event.target === this.detailBackdropTarget) {
      this.closeDetail()
    }
    this._backdropMousedown = false
  }

  prevFile() {
    const nav = this._navOrder()
    const idx = nav.indexOf(String(this._activeFileId))
    if (idx > 0) this._openDetail(nav[idx - 1])
  }

  nextFile() {
    const nav = this._navOrder()
    const idx = nav.indexOf(String(this._activeFileId))
    if (idx >= 0 && idx < nav.length - 1) this._openDetail(nav[idx + 1])
  }

  // Page the open PDF to the next/previous doc in the list without closing.
  // replaceTab: arrowing swaps the sheet inside the CURRENT tab (Larry
  // 2026-08-20 — hunting with next/prev used to stack a new tab per press).
  viewerPrev() {
    const nav = this._navOrder()
    const idx = nav.indexOf(String(this._viewerFileId))
    if (idx > 0) this._openInViewer(nav[idx - 1], { replaceTab: true })
  }

  viewerNext() {
    const nav = this._navOrder()
    const idx = nav.indexOf(String(this._viewerFileId))
    if (idx >= 0 && idx < nav.length - 1) this._openInViewer(nav[idx + 1], { replaceTab: true })
  }

  // Hover thumbnail on the sheet list (Larry 2026-08-20).
  sheetPreviewShow(event) {
    const el = event.currentTarget
    // The server-made page-1 picture when there is one (S2-14): instant, no
    // PDF download; the pdf.js rasterizer is the fallback.
    showSheetPreview(el, el.dataset.pdfUrl, el.dataset.thumbLabel, el.dataset.thumbUrl)
  }

  sheetPreviewHide() { hideSheetPreview() }

  // ── Delete ────────────────────────────────────────────────────────

  showDeleteConfirm() {
    this._deleteTarget = { type: "single", id: this._activeFileId }
    this.deleteMessageTarget.textContent = this._deleteMessageFor(this._activeFileId)
    this.deleteModalTarget.classList.remove("hidden")
  }

  // Deleting a combined book also deletes its extracted sheets — say so.
  // Versions (Larry 2026-09-15: "do we know exactly which ones these are?")
  // are named in full, and deleting the current one says which version
  // takes its place.
  _deleteMessageFor(id) {
    const f = (this._filesData || {})[String(id)]
    if (f && f.combined) {
      const kids = Object.values(this._filesData || {}).filter(o => o && String(o.combined_parent_id) === String(id)).length
      if (kids > 0) return `This is a combined file — deleting it also deletes its ${kids} extracted sheet${kids === 1 ? "" : "s"}. Are you sure?`
    }
    if (f && f.status === "superseded") {
      return `Delete ${this._versionWords(f)}? The other versions of ${f.sheet_number || "this sheet"} stay as they are.`
    }
    const older = f ? (f.versions || []) : []
    if (f && older.length) {
      const prev = older.find(v => String(v.id) !== String(f.id)) || older[0]
      return `Delete ${this._versionWords(f)}? It is the current version of ${f.sheet_number || "this sheet"} — ${prev ? this._versionWords(prev) : "the previous version"} becomes current again.`
    }
    return "Are you sure you want to delete this file?"
  }

  // "version 2 (Sep 15, 2026, Larry Kloepfer, from Bluebeam)"
  _versionWords(f) {
    const bits = [f.formatted_date, f.uploader_name, f.from_bluebeam ? "from Bluebeam" : null].filter(Boolean)
    return `version ${f.revision_number || "—"}${bits.length ? ` (${this._escapeHtml(bits.join(", "))})` : ""}`
  }

  _versionLabel(file, textColor) {
    const rev = file.revision_number ? this._escapeHtml(String(file.revision_number)) : "—"
    const meta = [file.formatted_date, file.uploader_name].filter(Boolean).map(x => this._escapeHtml(x)).join(" · ")
    const chip = file.from_bluebeam
      ? '<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium bg-blue-100 text-blue-700 ml-1.5" title="Saved from a Bluebeam session">Bluebeam</span>'
      : ""
    return `<span class="${textColor}">Version ${rev}</span>${meta ? `<span class="text-gray-400 ml-1.5">${meta}</span>` : ""}${chip}`
  }

  hideDeleteConfirm() {
    this.deleteModalTarget.classList.add("hidden")
    this._deleteTarget = null
  }

  async confirmDelete() {
    if (!this._deleteTarget) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      if (this._deleteTarget.type === "single") {
        const id = this._deleteTarget.id
        await fetch(`/projects/${this.projectIdValue}/drawings/${id}`, {
          method: "DELETE",
          headers: { "X-CSRF-Token": token, "Accept": "application/json" },
          credentials: "same-origin"
        })
        if (this._modalOpen && String(this._activeFileId) === String(id)) {
          this.closeDetail()
        }
        this._showToast("File deleted")
        // Reload so counts (breadcrumb, discipline tabs, sidebar) refresh and
        // any sheets orphaned by deleting a combined parent re-render.
        this._resetAndLoad()
      } else if (this._deleteTarget.type === "bulk") {
        const ids = Array.from(this._selectedIds).map(Number)
        await fetch(`/projects/${this.projectIdValue}/drawings/bulk_destroy`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token, "Accept": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ ids })
        })
        this._selectedIds.clear()
        this._updateBulkBar()
        this._showToast(`${ids.length} file(s) deleted`)
        this._resetAndLoad()
      }
    } catch (error) {
      console.error("[DrawingsSpecs] Delete failed:", error)
      this._showToast("Delete failed", "error")
    }

    this.hideDeleteConfirm()
  }

  // ── Wrong document type modal ─────────────────────────────────────

  _showWrongTypeModal() {
    if (this.hasWrongTypeLinkTarget) {
      this.wrongTypeLinkTarget.href = this.specificationsPageUrlValue
    }
    this.wrongTypeModalTarget.classList.remove("hidden")
  }

  hideWrongTypeModal() {
    this.wrongTypeModalTarget.classList.add("hidden")
  }

  // ── Upload Modal ──────────────────────────────────────────────────

  async openUploadModal() {
    this._uploadFiles = []
    this._splitStates = []
    if (this.hasUploadSheetRowsTarget) this.uploadSheetRowsTarget.innerHTML = ""
    if (this.hasUploadSheetTableTarget) this.uploadSheetTableTarget.classList.add("hidden")
    this.uploadSubmitBtnTarget.disabled = true
    this.uploadModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"

    // Reset set mode to "new"
    if (this.hasUploadSetModeSelectTarget) this.uploadSetModeSelectTarget.value = "new"
    if (this.hasUploadNewSetFieldsTarget) { this.uploadNewSetFieldsTarget.classList.remove("hidden"); this.uploadNewSetFieldsTarget.classList.add("flex") }
    if (this.hasUploadExistingSetFieldsTarget) { this.uploadExistingSetFieldsTarget.classList.add("hidden"); this.uploadExistingSetFieldsTarget.classList.remove("flex") }
    if (this.hasUploadSetNameInputTarget) this.uploadSetNameInputTarget.value = ""
    if (this.hasUploadSetDateInputTarget) this.uploadSetDateInputTarget.value = new Date().toISOString().slice(0, 10)
    if (this.hasUploadSetTypeSelectTarget) this.uploadSetTypeSelectTarget.value = ""
    if (this.hasUploadSetNumberInputTarget) this.uploadSetNumberInputTarget.value = ""
    this._setTypeTouched = false

    // Fetch drawing sets for "existing" dropdown
    await this._fetchDrawingSets()
  }

  _isUploadModalOpen() {
    return this.hasUploadModalTarget && !this.uploadModalTarget.classList.contains("hidden")
  }

  closeUploadModal() {
    this.uploadModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this._uploadFiles = []
    this._splitCancelled = true
    clearInterval(this._aiTimerInterval)
    this.uploadAiStatusTarget.classList.add("hidden")
    this.uploadAiStatusTarget.classList.remove("flex")
  }

  toggleSetMode(event) {
    const mode = event.currentTarget.value
    if (mode === "new") {
      this.uploadNewSetFieldsTarget.classList.remove("hidden")
      this.uploadNewSetFieldsTarget.classList.add("flex")
      this.uploadExistingSetFieldsTarget.classList.add("hidden")
      this.uploadExistingSetFieldsTarget.classList.remove("flex")
    } else {
      this.uploadNewSetFieldsTarget.classList.add("hidden")
      this.uploadNewSetFieldsTarget.classList.remove("flex")
      this.uploadExistingSetFieldsTarget.classList.remove("hidden")
      this.uploadExistingSetFieldsTarget.classList.add("flex")
    }
  }

  clickDropZone() {
    this.fileInputTarget.click()
  }

  async addFiles(event) {
    const files = event.target ? event.target.files : event
    if (!files || files.length === 0) return

    const added = []
    for (const file of files) {
      const ext = file.name.split(".").pop()?.toLowerCase()
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        this._showToast(`File type .${ext} is not allowed`, "error")
        continue
      }
      if (file.size > MAX_UPLOAD_SIZE) {
        this._showToast(`${file.name} exceeds 250MB limit`, "error")
        continue
      }
      const id = Date.now() + Math.round(Math.random() * 1000)
      const entry = { id, file, sheetNumber: "", title: "", discipline: "" }
      this._uploadFiles.push(entry)
      added.push(entry)
      if (ext !== "pdf") this._renderUploadSheetRow(id, file)
    }

    if (this.hasUploadSheetTableTarget && this.uploadSheetRowsTarget.children.length > 0) {
      this.uploadSheetTableTarget.classList.remove("hidden")
    }
    this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
    this._updateUploadBtnText()
    this._suggestSetName()
    this._autoFillSetType()
    if (event.target) event.target.value = ""

    // Keep button disabled while AI analyzes PDFs
    if (this._uploadFiles.some(uf => !uf._extracted && uf.file.name.split(".").pop()?.toLowerCase() === "pdf")) {
      this.uploadSubmitBtnTarget.disabled = true
      this.uploadSubmitBtnTarget.innerHTML = `<span class="inline-flex items-center gap-1.5"><svg class="w-3.5 h-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Extracting</span>`
    }

    // S2-13: a file that's already in this job's drawings — ask before the
    // split/AI work starts, so a second copy of Addendum 1 never lands.
    await this._skipAlreadyUploaded(added)
    if (this._uploadFiles.length === 0) {
      this.uploadSubmitBtnTarget.disabled = true
      this._updateUploadBtnText()
      return
    }

    // Check for combined PDFs first, then AI extraction for remaining
    this._checkForCombinedPdfs()
  }

  removeUploadFile(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    this._uploadFiles = this._uploadFiles.filter(f => f.id !== id)
    this._removeUploadRow(id)
    if (this._uploadFiles.length === 0 && this.hasUploadSheetTableTarget) {
      this.uploadSheetTableTarget.classList.add("hidden")
    }
    this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
    this._updateUploadBtnText()
  }

  uploadSheetNumberChanged(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (!uf) return
    uf.sheetNumber = event.currentTarget.value.trim()
    // Another sheet number: the box comes back on blur if that sheet has markups.
    this._showCopyMarkupsBox(id, null)

    // Auto-detect discipline
    const disc = detectDiscipline(uf.sheetNumber)
    if (disc) {
      uf.discipline = disc
      const discInput = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${id}"] [data-discipline-input]`)
      if (discInput) discInput.value = disc
    }
  }

  async uploadSheetNumberBlur(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (!uf || !uf.sheetNumber) return

    // Check if sheet exists
    const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${id}"]`)
    if (!row) return
    const sheetInput = event.currentTarget

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.checkSheetsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ sheet_numbers: [uf.sheetNumber], issued_date: this._uploadIssuedDate() })
      })
      const results = await res.json()
      const result = results[0]
      if (result && result.exists && result.title && !uf.title) {
        uf.title = result.title
        const titleInput = row.querySelector('[data-title-input]')
        if (titleInput) titleInput.value = result.title
      }
      this._paintSheetCheck(uf, row, sheetInput, result)
    } catch (e) {
      console.error("[DrawingsSpecs] check_sheets failed:", e)
    }
  }

  uploadTitleChanged(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (uf) uf.title = event.currentTarget.value.trim()
  }

  uploadDisciplineChanged(event) {
    const id = parseInt(event.currentTarget.dataset.uploadId)
    const uf = this._uploadFiles.find(f => f.id === id)
    if (uf) uf.discipline = event.currentTarget.value
  }

  // Primary action + the two dropdown variants. mode: "both" (default) |
  // "combined" (just the combined original) | "extracted" (just the sheets).
  submitUpload() { this._doUpload("both") }
  submitUploadCombinedOnly() { this._doUpload("combined") }
  submitUploadExtractedOnly() { this._doUpload("extracted") }

  toggleUploadMenu(event) {
    if (event) event.stopPropagation()
    if (this.hasUploadMenuTarget) this.uploadMenuTarget.classList.toggle("hidden")
  }

  _closeUploadMenu() {
    if (this.hasUploadMenuTarget) this.uploadMenuTarget.classList.add("hidden")
  }

  async _doUpload(mode = "both") {
    this._closeUploadMenu()
    let files = this._uploadFiles
    if (this.builderValue && this._uploadFiles.some(uf => uf._isCombined)) {
      if (mode === "combined") files = this._uploadFiles.filter(uf => uf._isCombined)
      else if (mode === "extracted") files = this._uploadFiles.filter(uf => !uf._isCombined)
    }
    if (files.length === 0) return
    this.uploadSubmitBtnTarget.disabled = true
    this.uploadSubmitBtnTarget.textContent = "Uploading..."
    // Disable the split-button's dropdown caret too while uploading.
    if (this.hasUploadMenuBtnTarget) this.uploadMenuBtnTarget.disabled = true

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      // 1. Get presigned URLs
      const fileMeta = files.map(uf => ({
        filename: uf.file.name,
        content_type: uf.file.type || "application/octet-stream",
        byte_size: uf.file.size
      }))

      const presignRes = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ files: fileMeta })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json()
        throw new Error(err.error || "Presign failed")
      }

      const { uploads } = await presignRes.json()

      // 2. Upload each file to S3
      const uploadPromises = uploads.map((upload, i) => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          const uf = files[i]
          const progressTrack = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"] [data-progress-track]`)
          const progressBar = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"] [data-progress-bar]`)
          if (progressTrack) { progressTrack.classList.remove("hidden"); progressTrack.classList.add("bg-gray-100") }

          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && progressBar) {
              const pct = Math.round((e.loaded / e.total) * 100)
              progressBar.style.width = `${pct}%`
            }
          }

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve({
                cache_name: upload.cache_name,
                filename: upload.filename,
                sheet_number: uf.sheetNumber,
                title: uf.title,
                discipline: uf.discipline,
                page_number: uf.pageNumber,
                combined: uf._isCombined === true,
                copy_markups: uf.copyMarkups === true,
                // S2-15: a spec section split off an addendum → Specs page.
                to_specs: uf._toSpecs === true,
                number: uf.sectionNumber,
                division: uf.division
              })
            } else {
              reject(new Error(`Upload failed: ${xhr.status}`))
            }
          }
          xhr.onerror = () => reject(new Error("Upload error"))

          xhr.open("PUT", upload.upload_url)
          xhr.send(uf.file)
        })
      })

      const allResults = await Promise.all(uploadPromises)
      // S2-15: the drawings register here; spec sections on the Specs page.
      const results = allResults.filter(r => !r.to_specs)
      const specResults = allResults.filter(r => r.to_specs)

      // 3. Build register payload
      const isNewSet = this.hasUploadSetModeSelectTarget && this.uploadSetModeSelectTarget.value === "new"
      const registerPayload = { files: results }

      if (isNewSet) {
        registerPayload.drawing_set_name = this.hasUploadSetNameInputTarget ? this.uploadSetNameInputTarget.value.trim() : ""
        registerPayload.drawing_set_issued_date = this.hasUploadSetDateInputTarget ? this.uploadSetDateInputTarget.value : ""
        registerPayload.drawing_set_issue_type = this.hasUploadSetTypeSelectTarget ? this.uploadSetTypeSelectTarget.value : ""
        registerPayload.drawing_set_issue_number = this.hasUploadSetNumberInputTarget ? this.uploadSetNumberInputTarget.value.trim() : ""
        // K7-21: "Just a new version" kept on a name that reads like "Addendum 1"
        // means no tag — say so, or the server would read the tag from the name.
        if (!registerPayload.drawing_set_issue_type && guessIssueFromName(registerPayload.drawing_set_name).type) {
          registerPayload.drawing_set_issue_type = "none"
          registerPayload.drawing_set_issue_number = ""
        }
      } else {
        registerPayload.drawing_set_id = this.hasUploadSetSelectTarget ? this.uploadSetSelectTarget.value : ""
      }

      let regData = {}
      if (results.length > 0) {
        const regResp = await fetch(this.registerUrlValue, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
          credentials: "same-origin",
          body: JSON.stringify(registerPayload)
        })
        if (!regResp.ok) throw new Error("Registration failed")
        regData = await regResp.json()
      }
      const uploadedIds = (regData.files || []).map(f => f.id)

      // S2-15: file the addendum's spec sections on the Specs page, in a spec
      // set named like the drawing set. A failure here never undoes the sheets.
      const specsFiled = specResults.length ? await this._fileSpecSections(specResults, token, isNewSet) : 0

      this.closeUploadModal()
      // S2-13: say when a set dated before the current version was filed under it.
      const older = regData.older_versions || 0
      let msg = older
        ? `${results.length} sheet(s) uploaded — ${older} filed as older version${older === 1 ? "" : "s"}`
        : `${results.length} sheet(s) uploaded`
      if (specsFiled) msg += ` · ${specsFiled} spec section${specsFiled === 1 ? "" : "s"} filed in Specs`
      this._showToast(msg)
      this._resetAndLoad()

      // Show distribution modal if we have uploaded file IDs
      if (uploadedIds.length > 0) {
        this._openDistributeModal(uploadedIds)
      }
    } catch (error) {
      console.error("[DrawingsSpecs] Upload failed:", error)
      this._showToast(error.message || "Upload failed", "error")
    } finally {
      this.uploadSubmitBtnTarget.disabled = false
      if (this.hasUploadMenuBtnTarget) this.uploadMenuBtnTarget.disabled = false
      this._updateUploadBtnText()
    }
  }

  // ── Addendum spec sections (S2-15, Jason/Starr) ───────────────────

  // Register the spec sections cut off an addendum on the Specs page. Their
  // set takes the drawing set's name and date ("Addendum 1"), reused if the
  // Specs page already has a set of that name. Returns how many were filed.
  async _fileSpecSections(specResults, token, isNewSet) {
    let setName = ""
    if (isNewSet) {
      setName = this.hasUploadSetNameInputTarget ? this.uploadSetNameInputTarget.value.trim() : ""
    } else {
      const id = this.hasUploadSetSelectTarget ? this.uploadSetSelectTarget.value : ""
      setName = (this._drawingSetsCache || []).find(s => String(s.id) === String(id))?.name || ""
    }
    try {
      const res = await fetch(this.specsRegisterUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({
          files: specResults.map(r => ({ cache_name: r.cache_name, filename: r.filename, number: r.number, title: r.title, division: r.division })),
          spec_set_name: setName,
          spec_set_issued_date: this._uploadIssuedDate(),
          reuse_spec_set: true
        })
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const n = (data.files || []).length
      track("Addendum Spec Sections Filed", { project_id: this.projectIdValue, sections: n })
      return n
    } catch (e) {
      console.error("[DrawingsSpecs] filing spec sections failed:", e)
      this._showToast("The spec sections couldn't be filed — upload them on the Specs page", "error")
      return 0
    }
  }

  // A spec-section row in the upload list: its number and title, and a
  // "Specs" pill where a sheet shows its discipline.
  _renderSpecSectionRow(entry) {
    const { id, file } = entry
    this.uploadSheetRowsTarget.insertAdjacentHTML("beforeend", `
      <tr data-upload-row="${id}" class="border-b border-gray-100">
        <td class="py-1.5 pr-3">
          <div class="flex items-center gap-1 min-w-0">
            <span class="text-[13px] text-gray-700 truncate" title="${this._escapeHtml(file.name)}">${this._escapeHtml(file.name)}</span>
          </div>
          <div class="relative h-1 rounded-full mt-1 hidden" data-progress-track>
            <div data-progress-bar class="h-full bg-brand rounded-full transition-all duration-200" style="width: 0%"></div>
          </div>
        </td>
        <td class="py-1.5 px-3">
          <input type="text" class="w-full text-[13px] rounded-lg border bg-blue-50 border-blue-200 focus:border-brand focus:ring-0 py-1 px-2 font-mono"
                 placeholder="08 71 00" value="${this._escapeHtml(entry.sectionNumber || "")}"
                 data-upload-id="${id}" data-action="input->drawings-specs#uploadSpecNumberChanged">
        </td>
        <td class="py-1.5 px-3">
          <input type="text" data-title-input class="w-full text-[13px] rounded-lg border bg-blue-50 border-blue-200 focus:border-brand focus:ring-0 py-1 px-2"
                 placeholder="Section title" value="${this._escapeHtml(entry.title || "")}"
                 data-upload-id="${id}" data-action="input->drawings-specs#uploadTitleChanged">
        </td>
        <td class="py-1.5 px-3">
          <span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium bg-violet-100 text-violet-700" title="Filed on the Specs page">Specs</span>
        </td>
        <td class="py-1.5 pl-3">
          <button type="button" class="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                  data-upload-id="${id}" data-action="click->drawings-specs#removeUploadFile" title="Don't file this section">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </td>
      </tr>`)
  }

  uploadSpecNumberChanged(event) {
    const uf = this._uploadFiles.find(f => f.id === parseInt(event.currentTarget.dataset.uploadId))
    if (uf) uf.sectionNumber = event.currentTarget.value.trim()
  }

  // NEW / REV beside each spec section, from the Specs page's own check.
  async _checkSpecSections(entries) {
    if (!this.specsCheckSectionsUrlValue || !entries.length) return
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.specsCheckSectionsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ section_numbers: entries.map(e => e.sectionNumber) })
      })
      if (!res.ok) return
      const results = await res.json()
      entries.forEach((e, i) => {
        const r = results[i]
        const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${e.id}"]`)
        const nameSpan = row?.querySelector("td:first-child span")
        if (!r || !nameSpan) return
        row.querySelector("[data-sheet-label]")?.remove()
        nameSpan.insertAdjacentHTML("afterend", r.exists
          ? `<span data-sheet-label class="text-[13px] font-bold text-amber-500 ml-1.5 whitespace-nowrap" title="Revises ${this._escapeHtml(e.sectionNumber)} on the Specs page">REV</span>`
          : `<span data-sheet-label class="text-[13px] font-bold text-emerald-500 ml-1.5 whitespace-nowrap">NEW</span>`)
      })
    } catch (e) {
      console.error("[DrawingsSpecs] check_sections failed:", e)
    }
  }

  // ── Already uploaded (S2-13, Jason/Starr) ─────────────────────────

  // Ask the server which of the new files are already in this job's drawings
  // (by file name, ignoring case, spacing and a browser's " (1)" copy), list
  // them in a modal and wait for Skip them / Upload anyway.
  async _skipAlreadyUploaded(entries) {
    if (!this.checkFilesUrlValue || !entries.length) return
    let dups = []
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.checkFilesUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ names: entries.map(e => e.file.name) })
      })
      if (!res.ok) return
      dups = (await res.json()).filter(r => r.exists)
    } catch (e) {
      console.error("[DrawingsSpecs] check_files failed:", e)
      return
    }
    if (!dups.length) return

    const modal = this.element.querySelector("[data-dup-file-modal]")
    const list = modal?.querySelector("[data-dup-file-list]")
    if (!modal || !list) return
    list.innerHTML = dups.map(d => {
      const where = [d.set_name, d.uploaded_on, d.uploader ? `by ${d.uploader}` : ""].filter(Boolean).join(" · ")
      return `<li class="px-3 py-2"><span class="font-medium text-gray-800 break-all">${this._escapeHtml(d.name)}</span>${where ? `<span class="block text-gray-500">${this._escapeHtml(where)}</span>` : ""}</li>`
    }).join("")
    modal.classList.remove("hidden")

    const skip = await new Promise(resolve => { this._dupResolve = resolve })
    modal.classList.add("hidden")
    this._dupResolve = null
    track("Duplicate Drawing Upload Caught", { project_id: this.projectIdValue, files: dups.length, skipped: skip })
    if (!skip) return

    const names = new Set(dups.map(d => d.name))
    const dropped = entries.filter(e => names.has(e.file.name))
    dropped.forEach(e => {
      this._uploadFiles = this._uploadFiles.filter(f => f.id !== e.id)
      this._removeUploadRow(e.id)
    })
    if (this._uploadFiles.length === 0 && this.hasUploadSheetTableTarget) this.uploadSheetTableTarget.classList.add("hidden")
    this._showToast(`${dropped.length} file${dropped.length === 1 ? "" : "s"} skipped — already uploaded`)
  }

  dupSkip() { this._dupResolve?.(true) }
  dupKeep() { this._dupResolve?.(false) }

  // ── Drop Zone Events ──────────────────────────────────────────────

  dragOverZone(event) {
    event.preventDefault()
    event.currentTarget.classList.add("border-brand", "bg-brand/5")
  }

  dragEnterZone(event) {
    event.preventDefault()
    event.currentTarget.classList.add("border-brand", "bg-brand/5")
  }

  dragLeaveZone(event) {
    event.currentTarget.classList.remove("border-brand", "bg-brand/5")
  }

  dropOnZone(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("border-brand", "bg-brand/5")
    this.addFiles(event.dataTransfer.files)
  }

  // ── Page-Level Drag ───────────────────────────────────────────────

  _setupPageDrag() {
    this._onDragOver = (e) => { e.preventDefault() }
    this._onDrop = (e) => {
      e.preventDefault()
      // Skip if the drop landed on the modal's dropzone (handled by dropOnZone)
      if (this._isUploadModalOpen()) return
      if (e.dataTransfer?.files?.length > 0) {
        this.openUploadModal()
        setTimeout(() => this.addFiles(e.dataTransfer.files), 100)
      }
    }

    document.addEventListener("dragover", this._onDragOver)
    document.addEventListener("drop", this._onDrop)
  }

  _teardownPageDrag() {
    if (this._onDragOver) document.removeEventListener("dragover", this._onDragOver)
    if (this._onDrop) document.removeEventListener("drop", this._onDrop)
  }

  // ── Export ZIP ────────────────────────────────────────────────────

  openExportModal() {
    if (!this._disciplineCounts) return

    // Populate discipline checkboxes
    const disciplines = this._disciplineCounts.filter(c => c.id !== null && c.count > 0)
    if (disciplines.length > 0) {
      this.exportDisciplinesTarget.innerHTML = disciplines.map(disc =>
        `<label class="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" class="form-checkbox rounded" style="accent-color: var(--color-brand)"
                 value="${this._escapeHtml(disc.id)}" checked data-export-discipline>
          <span class="text-sm text-gray-700">${this._escapeHtml(disc.name)} <span class="text-gray-400">(${disc.count})</span></span>
        </label>`
      ).join("")
    } else {
      this.exportDisciplinesTarget.innerHTML = '<p class="text-sm text-gray-400">No disciplines found</p>'
    }

    // Populate drawing set dropdown (only sets that still have files)
    const sets = this._setsWithFiles(this._drawingSetsCache)
    this.exportSetSelectTarget.innerHTML = `<option value="">All Sets</option>` +
      sets.map(s => `<option value="${s.id}">${this._escapeHtml(s.name)}</option>`).join("")

    // Reset historical checkbox
    this.exportHistoricalTarget.checked = false

    // Update file count
    this._updateExportFileCount()

    // Listen for changes
    this.exportDisciplinesTarget.addEventListener("change", this._updateExportFileCount.bind(this))
    this.exportSetSelectTarget.addEventListener("change", this._updateExportFileCount.bind(this))
    this.exportHistoricalTarget.addEventListener("change", this._updateExportFileCount.bind(this))

    this.exportModalTarget.classList.remove("hidden")
  }

  hideExportModal() {
    this.exportModalTarget.classList.add("hidden")
  }

  async submitExport() {
    const disciplines = [...this.exportDisciplinesTarget.querySelectorAll("[data-export-discipline]:checked")]
      .map(cb => cb.value)
    const drawingSetId = this.exportSetSelectTarget.value
    const includeHistorical = this.exportHistoricalTarget.checked

    const body = new URLSearchParams()
    if (disciplines.length > 0) {
      disciplines.forEach(d => body.append("disciplines[]", d))
    }
    if (drawingSetId) body.set("drawing_set_id", drawingSetId)
    if (includeHistorical) body.set("include_historical", "true")

    this.exportSubmitBtnTarget.disabled = true
    this.exportSubmitBtnTarget.textContent = "Exporting..."

    try {
      const resp = await fetch(this.exportZipUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body
      })

      if (resp.ok) {
        this.hideExportModal()
        this._showToast("Your export is being prepared. You will receive an email with the download link.")
      } else {
        this._showToast("Export failed. Please try again.", "error")
      }
    } catch {
      this._showToast("Export failed. Please try again.", "error")
    } finally {
      this.exportSubmitBtnTarget.disabled = false
      this.exportSubmitBtnTarget.textContent = "Export ZIP"
    }
  }

  // Ask the server for the real export count + stale-set check for the
  // current selection (set + disciplines + historical). Debounced so rapid
  // checkbox toggles don't fire a request each. Falls back to the cached
  // discipline counts if the request fails.
  _updateExportFileCount() {
    if (!this.hasExportFileCountTarget) return
    clearTimeout(this._exportCountTimeout)
    this._exportCountTimeout = setTimeout(() => this._fetchExportCount(), 150)
  }

  async _fetchExportCount() {
    const disciplines = [...this.exportDisciplinesTarget.querySelectorAll("[data-export-discipline]:checked")].map(cb => cb.value)
    const drawingSetId = this.exportSetSelectTarget?.value || ""
    const includeHistorical = this.exportHistoricalTarget?.checked || false

    if (!this.exportCountUrlValue) return this._renderExportCountFallback(disciplines)

    const params = new URLSearchParams()
    disciplines.forEach(d => params.append("disciplines[]", d))
    if (drawingSetId) params.set("drawing_set_id", drawingSetId)
    if (includeHistorical) params.set("include_historical", "true")

    // Token guards against an out-of-order response overwriting a newer one.
    const token = (this._exportCountToken = (this._exportCountToken || 0) + 1)
    try {
      const resp = await fetch(`${this.exportCountUrlValue}?${params.toString()}`, {
        headers: { Accept: "application/json" }, credentials: "same-origin"
      })
      const data = await resp.json()
      if (token !== this._exportCountToken) return
      this._renderExportCount(data.count || 0, data.stale || 0)
    } catch (_) {
      this._renderExportCountFallback(disciplines)
    }
  }

  _renderExportCount(count, stale) {
    this.exportFileCountTarget.textContent = `${count} file${count !== 1 ? "s" : ""} selected`
    if (this.hasExportStaleWarningTarget) {
      const show = stale > 0
      this.exportStaleWarningTarget.classList.toggle("hidden", !show)
      if (show && this.hasExportStaleTextTarget) {
        this.exportStaleTextTarget.textContent =
          `${stale} sheet${stale !== 1 ? "s have" : " has"} a newer revision in another set — choose “All Sets” to include the latest.`
      }
    }
  }

  // Offline fallback: approximate from the cached current-revision counts.
  _renderExportCountFallback(disciplines) {
    if (!this._disciplineCounts) return
    const all = this._disciplineCounts.filter(c => c.id !== null && c.count > 0)
    let count = 0
    if (disciplines.length === 0 || disciplines.length === all.length) {
      count = (this._disciplineCounts.find(c => c.id === null)?.count) || 0
    } else {
      disciplines.forEach(d => { count += (this._disciplineCounts.find(c => c.id === d)?.count) || 0 })
    }
    this._renderExportCount(count, 0)
  }

  // ── Export Compiled PDF ───────────────────────────────────────────

  async submitCompiledPdf() {
    const disciplines = [...this.exportDisciplinesTarget.querySelectorAll("[data-export-discipline]:checked")]
      .map(cb => cb.value)
    const drawingSetId = this.exportSetSelectTarget.value
    const includeHistorical = this.exportHistoricalTarget.checked

    const body = new URLSearchParams()
    if (disciplines.length > 0) {
      disciplines.forEach(d => body.append("disciplines[]", d))
    }
    if (drawingSetId) body.set("drawing_set_id", drawingSetId)
    if (includeHistorical) body.set("include_historical", "true")

    this.exportCompiledBtnTarget.disabled = true
    this.exportSubmitBtnTarget.disabled = true
    this.exportCompiledBtnTarget.textContent = "Compiling..."

    try {
      const resp = await fetch(this.exportCompiledPdfUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body
      })

      if (!resp.ok) {
        this._showToast("Export failed. Please try again.", "error")
        return
      }

      const { token } = await resp.json()
      this.hideExportModal()
      this._showCompilingToast("Compiling drawings into a single PDF...")
      this._pollCompiledPdf(token)
    } catch {
      this._showToast("Export failed. Please try again.", "error")
    } finally {
      this.exportCompiledBtnTarget.disabled = false
      this.exportSubmitBtnTarget.disabled = false
      this.exportCompiledBtnTarget.textContent = "Export Compiled PDF"
    }
  }

  async _pollCompiledPdf(token, attempts = 0) {
    if (attempts > 60) {
      this._hideCompilingToast()
      this._showToast("PDF compilation is taking longer than expected. Please try again.", "error")
      return
    }

    await new Promise(r => setTimeout(r, 2000))

    try {
      const resp = await fetch(`${this.compiledPdfStatusUrlValue}?token=${token}`)
      const data = await resp.json()

      if (data.status === "processing") {
        this._pollCompiledPdf(token, attempts + 1)
      } else if (data.status === "ready") {
        this._hideCompilingToast()
        this._showToast("Compiled PDF ready — downloading now.")
        const blob = await fetch(data.url).then(r => r.blob())
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = data.filename || "drawings-compiled.pdf"
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      } else {
        this._hideCompilingToast()
        this._showToast(data.message || "PDF compilation failed.", "error")
      }
    } catch {
      this._hideCompilingToast()
      this._showToast("Failed to check compilation status.", "error")
    }
  }

  _showCompilingToast(message) {
    this._hideCompilingToast()
    const el = document.createElement("div")
    el.id = "compiled-pdf-generating-toast"
    el.className = "flash-toast-info"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "0")
    el.setAttribute("role", "status")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = `<svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>`
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _hideCompilingToast() {
    const existing = document.getElementById("compiled-pdf-generating-toast")
    if (existing) existing.remove()
  }

  // ── Row Menu ─────────────────────────────────────────────────────

  toggleRowMenu(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const menu = btn.nextElementSibling

    // Close any other open menus
    this.rowMenuTargets.forEach(m => { if (m !== menu) m.classList.add("hidden") })

    menu.classList.toggle("hidden")

    // Position fixed menu — below the button when space allows, above otherwise.
    // Reserves bottom margin so the Intercom bubble (production) doesn't cover it.
    if (!menu.classList.contains("hidden")) {
      const rect = btn.getBoundingClientRect()
      const menuHeight = menu.offsetHeight
      const gap = 4
      const intercomBuffer = 96
      const spaceBelow = window.innerHeight - rect.bottom - intercomBuffer
      const spaceAbove = rect.top

      const flipUp = spaceBelow < menuHeight + gap && spaceAbove > spaceBelow
      const top = flipUp
        ? Math.max(8, rect.top - menuHeight - gap)
        : rect.bottom + gap
      menu.style.top = `${top}px`
      menu.style.left = `${rect.right - menu.offsetWidth}px`

      const close = (e) => {
        if (!menu.contains(e.target) && e.target !== btn) {
          menu.classList.add("hidden")
          document.removeEventListener("click", close)
        }
      }
      document.addEventListener("click", close)
    }
  }

  // ── Row-level actions ─────────────────────────────────────────────

  // ── Move to Specs (Phelipe 2026-09-14) ────────────────────────
  // The server re-runs the same sort-and-split an upload gets on the other
  // page, so the file arrives organized there; the rows here go to the trash.

  rowMove(event) {
    event.stopPropagation()
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))
    const id = event.currentTarget.dataset.fileId
    const file = this._filesData[String(id)]
    if (!file) return
    this._moveTarget = id
    if (this.hasMoveMessageTarget) this.moveMessageTarget.textContent = this._moveMessageFor(file)
    if (this.hasMoveModalTarget) this.moveModalTarget.classList.remove("hidden")
  }

  hideMoveConfirm() {
    if (this.hasMoveModalTarget) this.moveModalTarget.classList.add("hidden")
    this._moveTarget = null
  }

  async confirmMove() {
    const id = this._moveTarget
    if (!id) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const resp = await fetch(`/projects/${this.projectIdValue}/drawings/__ID__/move_to_specs`.replace("__ID__", id), {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Accept": "application/json" },
        credentials: "same-origin"
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.message || `HTTP ${resp.status}`)
      if (this._modalOpen && String(this._activeFileId) === String(id)) this.closeDetail()
      this._showToast(data.split ? "Moved to Specs and split into sections" : "Moved to Specs")
      this._resetAndLoad()
    } catch (error) {
      console.error("[drawings-specs] Move failed:", error)
      this._showToast(error.message || "Move failed", "error")
    }
    this.hideMoveConfirm()
  }

  _moveMessageFor(file) {
    const name = file.title || file.file_name || "this file"
    const pack = file.combined ? " and everything cut from it" : ""
    return `Move "${name}"${pack} to the Specs page? It will be organized there like an upload.`
  }

  rowDelete(event) {
    event.stopPropagation()
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))
    const id = event.currentTarget.dataset.fileId
    this._deleteTarget = { type: "single", id }
    this.deleteMessageTarget.textContent = this._deleteMessageFor(id)
    this.deleteModalTarget.classList.remove("hidden")
  }

  // ── Edit ────────────────────────────────────────────────────────

  rowEdit(event) {
    event.stopPropagation()
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))
    const id = event.currentTarget.dataset.fileId
    const file = this._filesData[String(id)]
    if (!file) return

    this._editFileId = id
    this.editSheetInputTarget.value = file.sheet_number || ""
    this.editTitleInputTarget.value = file.title || ""
    this.editDisciplineSelectTarget.value = file.discipline || ""
    const fp = this.editReceivedInputTarget._flatpickr
    if (fp) { fp.setDate(file.received_date || "", true) }
    else { this.editReceivedInputTarget.value = file.received_date || "" }

    // Populate set dropdown from cached drawing sets
    const sets = this._drawingSetsCache || []
    this.editSetSelectTarget.innerHTML = sets.map(s =>
      `<option value="${s.id}" ${String(s.id) === String(file.drawing_set_id) ? "selected" : ""}>${this._escapeHtml(s.name)}</option>`
    ).join("")
    this._fillEditSetIssue()

    this.editModalTarget.classList.remove("hidden")
  }

  // The Edit window's Set type + Number follow the picked set.
  editSetChanged() { this._fillEditSetIssue() }

  _fillEditSetIssue() {
    if (!this.hasEditSetTypeSelectTarget) return
    const set = (this._drawingSetsCache || []).find(s => String(s.id) === String(this.editSetSelectTarget.value))
    this.editSetTypeSelectTarget.value = set?.issue_type || ""
    this.editSetNumberInputTarget.value = set?.issue_number || ""
    const off = !set
    this.editSetTypeSelectTarget.disabled = off
    this.editSetNumberInputTarget.disabled = off
  }

  hideEditModal() {
    this.editModalTarget.classList.add("hidden")
    this._editFileId = null
  }

  async confirmEdit() {
    if (!this._editFileId) return
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const id = this._editFileId

    try {
      const res = await fetch(`/projects/${this.projectIdValue}/drawings/${id}/update_sheet`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token, "Accept": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          sheet_number: this.editSheetInputTarget.value.trim(),
          title: this.editTitleInputTarget.value.trim(),
          discipline: this.editDisciplineSelectTarget.value,
          drawing_set_id: this.editSetSelectTarget.value,
          received_date: this.editReceivedInputTarget.value,
          ...(this.hasEditSetTypeSelectTarget && this.editSetSelectTarget.value ? {
            set_issue_type: this.editSetTypeSelectTarget.value,
            set_issue_number: this.editSetNumberInputTarget.value.trim()
          } : {})
        })
      })

      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()

      if (data.file) {
        this._filesData[String(id)] = data.file
        const oldRow = this.tableBodyTarget.querySelector(`tr[data-file-id="${id}"]`)
        if (oldRow) oldRow.outerHTML = this._renderRow(data.file)
        if (this.hasGridContainerTarget) {
          const oldCard = this.gridContainerTarget.querySelector(`[data-file-id="${id}"]`)
          if (oldCard) oldCard.outerHTML = this._renderCard(data.file)
        }
        if (this._modalOpen && String(this._activeFileId) === String(id)) {
          this._renderDetailContent(data.file)
        }
      }

      // The set's tag changed: every loaded sheet of that set shows it now.
      if (data.set) {
        const setId = String(data.set.id)
        const cached = (this._drawingSetsCache || []).find(s => String(s.id) === setId)
        if (cached) Object.assign(cached, { issue_tag: data.set.issue_tag, issue_type: data.set.issue_type, issue_number: data.set.issue_number })
        Object.values(this._filesData).forEach(f => {
          if (String(f.drawing_set_id) === setId) f.issue_tag = data.set.issue_tag
        })
        if (this.currentViewValue === "list") this._reRenderTable()
        else this._renderGrid()
      }

      this._showToast("File updated")
    } catch (error) {
      console.error("[DrawingsSpecs] Edit failed:", error)
      this._showToast("Update failed", "error")
    }

    this.hideEditModal()
  }

  // ── PDF Viewer ────────────────────────────────────────────────────

  openPdfViewer(event) {
    const fileId = event.currentTarget.dataset.fileId
    const file = this._filesData[String(fileId)]
    if (!file) return

    const pdfController = this.element.querySelector('[data-controller~="action-item-pdf-viewer"]')
    if (!pdfController) return

    const ctrl = this.application.getControllerForElementAndIdentifier(pdfController, "action-item-pdf-viewer")
    if (ctrl) {
      const dataUrl = this.pdfViewerBaseUrlValue.replace(/\/0\//, `/${fileId}/`)
      const bluebeamEnabled = this.bluebeamAllowedValue && this.hasBluebeamOpenUrlTemplateValue && this._bluebeamEligible(file)
      ctrl.open({ dataUrl, filename: file.file_name, punchlistId: fileId, bluebeamEnabled, version: file.combined ? null : file.revision_number, versionCurrent: file.status === "current" })
    }
  }

  // ── Private: Data Loading ─────────────────────────────────────────

  async _loadPage(page) {
    if (this._loading || this._isLastPage) return
    this._loading = true
    const seq = (this._loadSeq ||= 1)

    try {
      const url = new URL(this.filesUrlValue, window.location.origin)
      url.searchParams.set("page", page)
      url.searchParams.set("status", this._statusFilter)

      if (this._filters.search && !this._aiSources) url.searchParams.set("search", this._filters.search)
      if (this._filters.date_from) url.searchParams.set("date_from", this._filters.date_from)
      if (this._filters.date_to) url.searchParams.set("date_to", this._filters.date_to)
      if (this._filters.uploader_id) url.searchParams.set("uploader_id", this._filters.uploader_id)
      if (this._activeDiscipline) url.searchParams.set("discipline", this._activeDiscipline)
      if (this._drawingSetFilter) url.searchParams.set("drawing_set_id", this._drawingSetFilter)
      // AI answer filter: only the documents the Ask-AI answer drew from.
      if (this._aiSources) {
        [...new Set(this._aiSources.map(s => String(s.id)))].forEach(id => url.searchParams.append("doc_ids[]", id))
      }

      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      if (seq !== this._loadSeq) return // a newer reload superseded this request

      if (this.hasSkeletonTarget) this.skeletonTarget.classList.add("hidden")

      if (page === 1) {
        this.tableBodyTarget.innerHTML = ""
        if (this.hasGridContainerTarget) this.gridContainerTarget.innerHTML = ""
        this._fileIds = []
        this._filesData = {}
        this._rowIndex = 0
        this._contentMatches = {}
      }

      // Per-document "matches inside the text" (count + page snippets),
      // present only while a search term is active.
      Object.assign(this._contentMatches ||= {}, data.content_matches || {})
      // AI answer filter: badges + snippets come from the answer's own quotes.
      if (this._aiSources) this._mergeAiMatches()
      if (page === 1 && this._filters.search && !this._aiSources) {
        track("Drawings Searched", {
          project_id: this.projectIdValue,
          results_count: data.total_count,
          documents_with_text_matches: Object.keys(data.content_matches || {}).length
        })
      }

      data.files.forEach(file => {
        this._fileIds.push(String(file.id))
        this._filesData[String(file.id)] = file
        // Also register nested prior versions so they can be selected and
        // compared even when the "Historical" view isn't toggled on.
        ;(file.versions || []).forEach(v => { this._filesData[String(v.id)] = v })

        // Thumbnails view appends cards progressively, one section per
        // discipline (S2-14); list view is (re)built as a tree below so
        // combined + version nesting + the expand/collapse arrows render on
        // first load, not just after a sort.
      })
      if (this.currentViewValue === "list") this._reRenderTable()
      else this._scheduleThumbPoll()

      // Update breadcrumb count
      const badge = document.getElementById("ds-breadcrumb-count")
      if (badge) badge.textContent = data.total_count

      // Keep the sidebar nav counter in sync (after delete/upload/etc.).
      this._updateNavCount(data.nav_count)

      // AI Radar deep link: open the cited document once it's loaded, with
      // the cited passage searched & highlighted in the viewer.
      if (this._aiOpenId && this._filesData[String(this._aiOpenId)]) {
        const openId = this._aiOpenId, openPage = this._aiOpenPage, openHl = this._aiOpenHl
        this._aiOpenId = null
        // Land ON the cited row first — scrolled to and marked — so the
        // answer's source is findable even after the viewer is closed
        // (Phelipe 2026-08-21).
        this._markCitedRow(openId)
        setTimeout(() => this._openInViewer(openId, { initialPage: openPage, searchTerm: openHl }), 250)
      }
      // ChatLog sources: the list filters to the cited document, the row
      // pulses, and the viewer opens on the cited page (Phelipe 2026-08-25).
      if (this._aiLocateId) {
        const locateId = this._aiLocateId
        const locatePage = this._aiOpenPage
        this._aiLocateId = null
        this._updateAiChip()
        this._markCitedRow(locateId)
        if (this._filesData[String(locateId)]) {
          setTimeout(() => this._openInViewer(locateId, { initialPage: locatePage }), 250)
        }
      }

      // Update discipline tabs + export + drawing set filter
      if (page === 1) {
        this._renderDisciplineTabs(data.discipline_counts)
        this._disciplineCounts = data.discipline_counts
        this._drawingSetsCache = data.drawing_sets || []
        this._populateDrawingSetFilter(data.drawing_sets || [])
      }

      // Handle empty state
      if (page === 1 && data.files.length === 0) {
        this.emptyTarget.classList.remove("hidden")
        this.tableContainerTarget.classList.add("hidden")
        this.hasGridContainerTarget && this.gridContainerTarget.classList.add("hidden")
        if (this.hasSentinelTarget) this.sentinelTarget.classList.add("hidden")
      } else {
        this.emptyTarget.classList.add("hidden")
        if (this.currentViewValue === "list") {
          this.tableContainerTarget.classList.remove("hidden")
        } else {
          this.hasGridContainerTarget && this.gridContainerTarget.classList.remove("hidden")
          this.hasGridContainerTarget && this.gridContainerTarget.classList.add("flex")
        }
      }
      if (this.currentViewValue !== "list") this._renderGrid()

      this._isLastPage = data.is_last_page
      this._page = page + 1

      if (!this._isLastPage && this.hasSentinelTarget) {
        this.sentinelTarget.classList.remove("hidden")
        this._observer.observe(this.sentinelTarget)
      } else if (this.hasSentinelTarget) {
        this.sentinelTarget.classList.add("hidden")
        this._observer.disconnect()
      }
    } catch (error) {
      console.error("[DrawingsSpecs] Failed to load page:", error)
      if (this.hasSkeletonTarget) this.skeletonTarget.classList.add("hidden")
      // Don't strand the user on a silently blank table — say what happened.
      if (page === 1 && seq === this._loadSeq && this.tableBodyTarget.children.length === 0) {
        this.tableBodyTarget.innerHTML =
          '<tr><td colspan="9" class="px-4 py-10 text-center text-[13px] text-gray-500">' +
          "Couldn't load the list — check your connection and try again." +
          "</td></tr>"
        if (this.hasTableContainerTarget) this.tableContainerTarget.classList.remove("hidden")
      }
    } finally {
      this._loading = false
    }
  }

  _resetAndLoad() {
    this._page = 1
    this._isLastPage = false
    this._loading = false
    // Stale-response guard: a reload invalidates any in-flight page fetch
    // so a slow older response can't clobber a newer one.
    this._loadSeq = (this._loadSeq || 0) + 1
    this._fileIds = []
    this._filesData = {}
    this._rowIndex = 0
    if (this._observer) this._observer.disconnect()
    this._stopThumbPoll()
    this.tableBodyTarget.innerHTML = ""
    if (this.hasGridContainerTarget) this.gridContainerTarget.innerHTML = ""
    // Show the loading skeleton instead of a silently blank table while
    // the (possibly slow) search/filter request is in flight.
    if (this.hasSkeletonTarget) this.skeletonTarget.classList.remove("hidden")
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
    this._loadPage(1)
  }

  // ── Private: Render Table Row ─────────────────────────────────────

  // Build the table body as a tree (Builder only; owners render flat):
  //   Full set PDFs (one folded group) · Discipline ▸ Sheet ▸ prior Versions
  // S2-13 (Jason, Starr): the sheets are ONE log grouped by discipline,
  // whichever upload they came in with; the combined PDFs (one per upload)
  // share a single folded row on top instead of each being a book of its own
  // with the sheets split between them. Version history is collapsed by
  // default (only the latest version shows).
  _renderRowsTree(sorted) {
    if (!this.builderValue) return sorted.map(f => this._renderRow(f)).join("")
    // A search / AI filter forces everything open so matches aren't hidden in
    // a collapsed set; otherwise each set's state comes from the user's saved
    // preference (combined sets default open, version history default closed).
    // A search / AI filter / discipline tab forces everything open so matches
    // aren't hidden in a collapsed group; otherwise each group's state comes
    // from the user's saved preference.
    const searching = !!(this._filters.search || this._aiSources || this._activeDiscipline)
    // Render a sheet; if it has prior versions, give it an arrow and nest those
    // versions (collapsed) beneath it. `parentOpen` is the whole ancestor chain
    // being expanded — a sheet inside a collapsed discipline stays hidden.
    const renderSheet = (f, opts = {}, parentOpen = true) => {
      const vers = f.versions || []
      if (!vers.length) return this._renderRow(f, { ...opts, hidden: !parentOpen })
      const key = `s${f.id}`
      const open = searching ? true : this._groupExpanded(key, false)
      let out = this._renderRow(f, { ...opts, hidden: !parentOpen, expandable: true, collapsed: !open, groupKey: key })
      vers.forEach(v => { out += this._renderRow(v, { child: true, grandchild: opts.grandchild, parentId: f.id, version: true, hidden: !(parentOpen && open) }) })
      return out
    }
    let html = ""
    const books = sorted.filter(f => f.combined)
    const sheets = sorted.filter(f => !f.combined)
    if (books.length) {
      const key = "books"
      const open = searching ? true : this._groupExpanded(key, false) // folded by default
      html += this._renderBooksGroupRow(books.length, !open, key)
      books.forEach(b => { html += this._renderRow(b, { child: true, parentId: "books", hidden: !open }) })
    }
    html += this._renderDisciplineGroups(sheets, null, 0, renderSheet, searching, true)
    return html
  }

  // S2-13: the one folded row holding every full-set PDF (one per upload),
  // shaped like a discipline group row.
  _renderBooksGroupRow(count, collapsed, groupKey) {
    const checkboxCol = this.canManageDocumentsValue
      ? `<td class="pl-3.5 pr-2 py-2 w-8 ${this._bulkMode ? "" : "hidden"}" data-checkbox-col></td>`
      : ""
    return `<tr class="bg-gray-50/80 border-b border-gray-200 cursor-pointer transition-colors hover:bg-brand/[0.04]"
                data-file-id="books" data-action="click->drawings-specs#groupRowClick">
      <td class="pl-2 pr-2 py-2 align-middle">
        <button type="button" onclick="event.stopPropagation()" data-action="click->drawings-specs#toggleCombinedRow" data-parent-id="books" data-group-key="${groupKey}" data-expanded="${collapsed ? "false" : "true"}" class="align-middle p-1.5 -m-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 cursor-pointer"><svg data-chevron class="w-4 h-4 inline transition-transform${collapsed ? " -rotate-90" : ""}" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg></button>
      </td>
      ${checkboxCol}
      <td class="px-2 xl:px-3 py-2 xl:py-2.5 whitespace-nowrap"><span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium bg-blue-100 text-blue-700">Combined</span></td>
      <td class="px-2 xl:px-3 py-2 xl:py-2.5 whitespace-nowrap"><span class="font-semibold text-gray-800">Full set PDFs</span> <span class="text-gray-400">· ${count} file${count === 1 ? "" : "s"}</span></td>
      <td class="px-2 xl:px-3 py-2 xl:py-2.5 whitespace-nowrap"></td>
      <td class="py-2 text-center"></td><td class="py-2 ds-set-col"></td><td class="py-2"></td><td class="py-2"></td>
    </tr>`
  }

  // Bucket sibling sheets into one group per discipline (conventional sheet
  // order), each holding its sheets. Mirrors the specs page's division groups.
  _renderDisciplineGroups(rows, parentId, depth, renderSheet, searching = false, parentOpen = true) {
    if (!rows.length) return ""
    const buckets = new Map() // discipline name -> sheets[]
    rows.forEach(r => {
      const key = r.discipline || "Other"
      if (!buckets.has(key)) buckets.set(key, [])
      buckets.get(key).push(r)
    })
    const orderOf = name => {
      if (name === "Other") return 9998
      const i = DISCIPLINE_ORDER.indexOf(name)
      return i === -1 ? 9000 : i
    }
    const keys = [...buckets.keys()].sort((a, b) => orderOf(a) - orderOf(b) || String(a).localeCompare(String(b)))

    // The sheet-number prefix shared by the group (C-1, C-2 → "C") — shown as a
    // small badge in the Sheet # column, like the specs series badge.
    const prefixOf = sheets => {
      for (const s of sheets) { const m = String(s.sheet_number || "").trim().match(/^([A-Za-z]+)/); if (m) return m[1].toUpperCase() }
      return ""
    }

    let html = ""
    keys.forEach((name, i) => {
      const sheets = buckets.get(name)
      // Default view: natural sheet-number order within the discipline (C-1,
      // C-2, … C-10), not the server's string order (which wedges C-10 before
      // C-2). An active column sort already ordered them, so leave it alone.
      if (!this._sort.key) sheets.sort((a, b) => (a.sheet_number || "").localeCompare(b.sheet_number || "", undefined, { numeric: true, sensitivity: "base" }))
      const gid = `discgrp-${parentId || "top"}-${i}`
      const groupKey = `g${parentId || "top"}:${name}` // stable per-user key
      const open = searching ? true : this._groupExpanded(groupKey, false) // collapsed by default
      html += this._renderDisciplineGroupRow({
        gid, parentId, name, prefix: prefixOf(sheets), count: sheets.length, depth,
        collapsed: !open, hidden: !parentOpen, groupKey
      })
      const childOpen = parentOpen && open
      const childOpts = depth > 0
        ? { child: true, grandchild: true, parentId: gid }
        : { child: true, parentId: gid }
      sheets.forEach(s => { html += renderSheet(s, childOpts, childOpen) })
    })
    return html
  }

  // Synthetic discipline header row (collapsed by default) — groups a
  // discipline's sheets; nested inside a combined set or at the top level.
  _renderDisciplineGroupRow({ gid, parentId, name, prefix, count, depth, collapsed = false, hidden = false, groupKey = "" }) {
    const checkboxCol = this.canManageDocumentsValue
      ? `<td class="pl-3.5 pr-2 py-2 w-8 ${this._bulkMode ? "" : "hidden"}" data-checkbox-col></td>`
      : ""
    const treeAttr = parentId ? `data-row-child-of="${parentId}"` : ""
    const color = DISCIPLINE_COLORS[name] || "bg-gray-100 text-gray-600"
    const prefixBadge = prefix
      ? `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium ${color}">${this._escapeHtml(prefix)}</span>`
      : ""
    // Discipline-column label so the parent row reads like its child sheets.
    const discBadge = (name && name !== "Other")
      ? `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium ${color}">${this._escapeHtml(name)}</span>`
      : ""
    return `<tr class="bg-gray-50/80 border-b border-gray-200 cursor-pointer transition-colors hover:bg-brand/[0.04] ${hidden ? "hidden" : ""}"
                ${treeAttr} data-file-id="${gid}"
                data-action="click->drawings-specs#groupRowClick">
      <td class="${depth > 0 ? "pl-6" : "pl-2"} pr-2 py-2 align-middle">
        <button type="button" onclick="event.stopPropagation()" data-action="click->drawings-specs#toggleCombinedRow" data-parent-id="${gid}" data-group-key="${groupKey}" data-expanded="${collapsed ? "false" : "true"}" class="align-middle p-1.5 -m-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 cursor-pointer"><svg data-chevron class="w-4 h-4 inline transition-transform${collapsed ? " -rotate-90" : ""}" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg></button>
      </td>
      ${checkboxCol}
      <td class="px-2 xl:px-3 py-2 xl:py-2.5 whitespace-nowrap">${prefixBadge}</td>
      <td class="px-2 xl:px-3 py-2 xl:py-2.5 whitespace-nowrap"><span class="font-semibold text-gray-800">${this._escapeHtml(name)}</span> <span class="text-gray-400">· ${count} sheet${count === 1 ? "" : "s"}</span></td>
      <td class="px-2 xl:px-3 py-2 xl:py-2.5 whitespace-nowrap">${discBadge}</td>
      <td class="py-2 text-center"></td><td class="py-2 ds-set-col"></td><td class="py-2"></td><td class="py-2"></td>
    </tr>`
  }

  // Clicking a group row expands/collapses its children — same as the arrow.
  groupRowClick(event) {
    const btn = event.currentTarget.querySelector("[data-parent-id]")
    if (btn) btn.click()
  }

  // Expand/collapse a row's children (combined → disciplines, discipline →
  // sheets, or sheet → versions). Collapsing hides ALL levels beneath so
  // re-expanding never surfaces an orphaned grandchild row.
  toggleCombinedRow(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const pid = btn.dataset.parentId
    const collapse = btn.dataset.expanded !== "false"
    if (collapse) {
      this._collapseDescendants(pid)
    } else {
      this.tableBodyTarget.querySelectorAll(`[data-row-child-of="${pid}"]`).forEach(r => r.classList.remove("hidden"))
    }
    btn.dataset.expanded = collapse ? "false" : "true"
    const chev = btn.querySelector("[data-chevron]")
    if (chev) chev.classList.toggle("-rotate-90", collapse)
    // Remember this group's new state for the next visit (per user).
    this._recordGroup(btn.dataset.groupKey, !collapse)
  }

  // Recursively hide every descendant of pid, resetting nested chevrons to
  // collapsed and closing any open search-snippet rows along the way (those
  // are inserted siblings, not children, so they'd otherwise stay behind).
  _collapseDescendants(pid) {
    this.tableBodyTarget.querySelectorAll(`[data-row-child-of="${pid}"]`).forEach(r => {
      r.classList.add("hidden")
      const childBtn = r.querySelector("button[data-parent-id]")
      if (childBtn) {
        childBtn.dataset.expanded = "false"
        childBtn.querySelector("[data-chevron]")?.classList.add("-rotate-90")
      }
      const cid = r.dataset.fileId
      if (!cid) return
      const matchRow = this.tableBodyTarget.querySelector(`[data-match-row-for="${cid}"]`)
      if (matchRow) {
        matchRow.remove()
        r.querySelector("[data-match-chevron]")?.classList.remove("rotate-180")
      }
      this._collapseDescendants(cid)
    })
  }

  _renderRow(file, opts = {}) {
    const isSuperseded = file.status === "superseded"
    const rowBg = isSuperseded ? "bg-gray-50" : "bg-white"
    this._rowIndex++
    const cell = "px-2 xl:px-3 py-2 xl:py-2.5 whitespace-nowrap overflow-hidden text-ellipsis"

    const checkboxCol = this.canManageDocumentsValue
      ? `<td class="pl-3.5 pr-2 py-2 w-8 ${this._bulkMode ? "" : "hidden"}" data-checkbox-col data-controller="modal-toggle" data-action="click->modal-toggle#stop">
           <input type="checkbox" class="form-checkbox rounded" style="accent-color: var(--color-brand)"
                  data-file-checkbox data-file-id="${file.id}"
                  data-action="change->drawings-specs#toggleFileSelect"
                  ${this._selectedIds.has(String(file.id)) ? "checked" : ""}>
         </td>`
      : ""

    const discBadge = file.discipline
      ? `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium ${DISCIPLINE_COLORS[file.discipline] || "bg-gray-100 text-gray-600"}">${this._escapeHtml(file.discipline)}</span>`
      : '<span class="text-gray-400">—</span>'

    const menuItems = this._rowMenuItems(file)


    const actionsCol = menuItems.trim()
      ? `<td class="py-2 text-center relative" data-controller="modal-toggle" data-action="click->modal-toggle#stop">
           <button type="button" class="p-1 rounded hover:bg-gray-100 cursor-pointer"
                   data-action="click->drawings-specs#toggleRowMenu"
                   data-file-id="${file.id}">
             <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>
           </button>
           <div class="hidden fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-44 whitespace-normal"
                data-drawings-specs-target="rowMenu">
             ${menuItems}
           </div>
         </td>`
      : `<td class="py-2"></td>`

    const textColor = isSuperseded ? "text-gray-400" : "text-gray-600"
    const sheetColor = isSuperseded ? "text-gray-400" : "text-gray-800"

    const treeAttr = opts.child ? `data-row-child-of="${opts.parentId}"` : ""
    const childBg = opts.child ? "bg-gray-50/50" : ""
    const chevCollapsed = opts.collapsed ? " -rotate-90" : ""
    const expandedAttr = opts.collapsed ? ' data-expanded="false"' : ""
    // The Sheet # column. Child rows (sheets / versions) get a wider left
    // gutter so a version-expand arrow — drawn in the fixed-width chevron
    // column to the left — has room and doesn't sit on top of the number.
    const numCell = opts.child ? "pl-8 pr-2 xl:pr-3 py-2 xl:py-2.5 whitespace-nowrap overflow-hidden text-ellipsis" : cell
    const sheetCellInner = file.combined
      ? '<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium bg-blue-100 text-blue-700">Combined</span>'
      : opts.version
        ? this._versionLabel(file, textColor)
        : `<span class="font-mono font-semibold ${sheetColor}">${file.sheet_number ? this._escapeHtml(file.sheet_number) : '<span class="text-gray-400 font-normal">—</span>'}</span>`

    // Search-inside-documents: result count on the left of matching rows; the
    // chevron flips when the row is expanded to show its snippets.
    const match = this._contentMatches?.[String(file.id)]
    const matchBadge = match
      ? `<span class="inline-flex items-center gap-1 rounded-full bg-brand/10 text-brand px-2 py-px text-[13px] font-semibold mr-2 align-middle" title="${match.count} result${match.count === 1 ? "" : "s"} inside this document — click to preview">${match.count}<svg data-match-chevron class="w-3 h-3 transition-transform" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg></span>`
      : ""
    // Hover thumbnail (Larry 2026-08-20): a small photo glyph beside the sheet
    // number — resting the mouse on it floats a page-1 preview of the sheet.
    const thumbIcon = file.pdf_url && !file.combined
      ? `<span class="inline-flex align-middle mr-1.5 p-0.5 rounded text-gray-400 hover:text-brand hover:bg-brand/10 cursor-zoom-in" data-pdf-url="${this._escapeHtml(file.pdf_url)}" data-thumb-url="${this._escapeHtml(file.thumb_url || "")}" data-thumb-label="${this._escapeHtml([file.sheet_number, file.title].filter(Boolean).join(" — "))}" data-action="mouseenter->drawings-specs#sheetPreviewShow mouseleave->drawings-specs#sheetPreviewHide click->drawings-specs#sheetPreviewHide"><svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Z"/></svg></span>`
      : ""
    return `<tr class="${rowBg} ${childBg} border-b border-gray-200 cursor-pointer transition-colors hover:bg-brand/[0.04] ${opts.hidden ? "hidden" : ""}"
                data-action="click->drawings-specs#openFile"
                data-file-id="${file.id}" ${treeAttr}>
      <td class="${opts.grandchild ? "pl-10" : (opts.child ? "pl-6" : "pl-2")} pr-2 py-2 align-middle">${opts.expandable
        ? `<button type="button" onclick="event.stopPropagation()" data-action="click->drawings-specs#toggleCombinedRow" data-parent-id="${file.id}"${opts.groupKey ? ` data-group-key="${opts.groupKey}"` : ""}${expandedAttr} class="align-middle p-1.5 -m-1.5 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 cursor-pointer" title="Show / hide"><svg data-chevron class="w-4 h-4 inline transition-transform${chevCollapsed}" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/></svg></button>`
        : ""}</td>
      ${checkboxCol}
      <td class="${numCell}">${matchBadge}${thumbIcon}${sheetCellInner}</td>
      <td class="${cell}">
        <span class="${file.title ? textColor : "text-gray-400"} truncate ${this._bluebeamChip(file) ? "inline-block align-middle max-w-[60%]" : "block"}">${this._escapeHtml(file.title || file.file_name || "—")}</span>${this._bluebeamChip(file)}
      </td>
      <td class="${cell}">${isSuperseded ? '<span class="text-gray-400">' + this._escapeHtml(file.discipline || "—") + '</span>' : discBadge}</td>
      <td class="${cell} text-center">
        <span class="inline-flex items-center gap-1.5"><span class="${textColor}">${file.revision_number ? `v${this._escapeHtml(file.revision_number)}` : "—"}</span>${file.combined ? "" : issueTagChip(file.issue_tag, file.drawing_set_name)}</span>
      </td>
      <td class="${cell} ${textColor} ds-set-col"><span class="truncate block">${this._escapeHtml(file.drawing_set_name || "—")}</span></td>
      <td class="${cell} ${textColor}">${this._escapeHtml(file.formatted_date || "")}</td>
      ${actionsCol}
    </tr>`
  }

  // ── Private: Render Grid Card ─────────────────────────────────────

  // The row menu (Compare / Bluebeam / Edit / Move / Delete) — shared by
  // the list rows and the Thumbnails cards.
  _rowMenuItems(file) {
    // Only offer "Compare to previous" when this sheet actually has an
    // older version to compare against.
    const compareItem = this._hasPreviousVersion(file)
      ? `<button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                     data-action="click->drawings-specs#compareToPrevious" data-file-id="${file.id}">Compare to previous</button>`
      : ""

    const bluebeamItems = this._bluebeamMenuItems(file)

    // "Move to Specs" (Phelipe 2026-09-14) — a file the bidding carry-over
    // put on the wrong page (hand uploads don't get it). Top-level rows only:
    // a split pack moves as a whole, so the sheets or sections cut from it
    // travel with their parent rather than one by one.
    const moveItem = (file.combined_parent_id || !file.from_bidding)
      ? ""
      : `<button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                 data-action="click->drawings-specs#rowMove" data-file-id="${file.id}">Move to Specs</button>`

    // Owners/consultants get the full row menu; builder/GC users get a menu
    // with only the Bluebeam actions (edit/delete/compare stay owner-only).
    return this.canManageDocumentsValue
      ? `${compareItem}
             ${bluebeamItems}
             <button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                     data-action="click->drawings-specs#rowEdit" data-file-id="${file.id}">Edit</button>
             ${moveItem}
             <button type="button" class="block w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                     data-action="click->drawings-specs#rowDelete" data-file-id="${file.id}">Delete</button>`
      : (this.bluebeamAllowedValue ? bluebeamItems : "")
  }

  _renderCard(file) {
    const isSuperseded = file.status === "superseded"
    const mutedClass = isSuperseded ? "opacity-60" : ""
    const checkboxHtml = this.canManageDocumentsValue
      ? `<div class="absolute top-2 left-2 z-10 ${this._bulkMode ? "" : "hidden"}" data-checkbox-col data-controller="modal-toggle" data-action="click->modal-toggle#stop">
           <input type="checkbox" class="form-checkbox rounded shadow-sm" style="accent-color: var(--color-brand)"
                  data-file-checkbox data-file-id="${file.id}"
                  data-action="change->drawings-specs#toggleFileSelect"
                  ${this._selectedIds.has(String(file.id)) ? "checked" : ""}>
         </div>`
      : ""

    const menuItems = this._rowMenuItems(file)
    const menuHtml = menuItems.trim()
      ? `<div class="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
              data-controller="modal-toggle" data-action="click->modal-toggle#stop">
           <button type="button" class="p-1 rounded-md bg-white/90 border border-gray-200 shadow-sm hover:bg-gray-50 cursor-pointer"
                   data-action="click->drawings-specs#toggleRowMenu" data-file-id="${file.id}">
             <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>
           </button>
           <div class="hidden fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-44 whitespace-normal"
                data-drawings-specs-target="rowMenu">
             ${menuItems}
           </div>
         </div>`
      : ""

    const sheetLabel = file.combined
      ? '<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium bg-blue-100 text-blue-700">Combined</span>'
      : `<span class="font-mono font-semibold text-[13px] ${isSuperseded ? "text-gray-400" : "text-gray-800"} truncate">${file.sheet_number ? this._escapeHtml(file.sheet_number) : '<span class="text-gray-400 font-normal">—</span>'}</span>`
    const rev = file.revision_number
      ? `<span class="text-[13px] text-gray-400 shrink-0">v${this._escapeHtml(file.revision_number)}</span>`
      : ""
    // What the sheet's set was issued as — "BID", "ADD-1", "RFI-071" — so a
    // super sees at a glance where this version came from.
    const tag = issueTagChip(file.issue_tag, file.drawing_set_name)
    const title = this._escapeHtml(file.title || file.file_name || "")
    const pending = file.thumb_pending && !file.thumb_url ? " data-thumb-pending" : ""

    return `<div class="relative w-[200px] max-w-full rounded-lg border border-gray-200 bg-white overflow-hidden cursor-pointer hover:shadow-md hover:border-brand/40 transition-all group ${mutedClass}"
                 data-action="click->drawings-specs#openFile"
                 data-file-id="${file.id}" data-grid-card${pending} title="${title}">
      ${checkboxHtml}
      ${menuHtml}
      <div class="aspect-[3/2] bg-gray-50 relative overflow-hidden" data-thumb-box>${this._cardThumbHtml(file)}</div>
      <div class="px-2.5 py-2 border-t border-gray-100">
        <div class="flex items-center justify-between gap-2 min-w-0">${sheetLabel}<span class="flex items-center gap-1.5 shrink-0">${tag}${rev}</span></div>
        <p class="text-[13px] text-gray-500 truncate mt-0.5">${title || "&nbsp;"}</p>
      </div>
    </div>`
  }

  // The picture box of a card: the page-1 picture when it exists, a quiet
  // "being made" note while the job renders it, or the file's icon.
  _cardThumbHtml(file) {
    if (file.thumb_url) {
      return `<img src="${this._escapeHtml(file.thumb_url)}" alt="" loading="lazy" class="absolute inset-0 w-full h-full object-contain bg-white">`
    }
    const icon = this._fileTypeIcon(file.extension, "w-9 h-9")
    const note = file.thumb_pending
      ? `<span class="mt-1.5 text-[13px] text-gray-400 animate-pulse">Making preview…</span>`
      : ""
    return `<div class="absolute inset-0 flex flex-col items-center justify-center text-gray-300">${icon}${note}</div>`
  }

  // S2-14 layout (Phelipe's picture 2026-10-23): one tinted box per
  // discipline — its pill and count on top, its cards inside — sitting side by
  // side in the conventional plan-set order (combined sets first). When a
  // discipline doesn't fit in what's left of a row, it starts there and its
  // remaining cards carry on in a full-width box of the same color below.
  // Rebuilt from the loaded files after every page load and on resize.
  _renderGrid() {
    if (!this.hasGridContainerTarget) return
    const cont = this.gridContainerTarget
    const W = cont.clientWidth
    if (!W) return

    const CARD = 200, GAP = 12, PAD = 12, BORDER = 1, BOX_GAP = 16
    const boxWidth = n => 2 * (PAD + BORDER) + n * CARD + (n - 1) * GAP
    const fits = room => Math.floor((room - 2 * (PAD + BORDER) + GAP) / (CARD + GAP))

    // Group the loaded sheets, keeping the server's sheet order inside each.
    const groups = new Map()
    this._fileIds.map(id => this._filesData[id]).filter(Boolean).forEach(f => {
      const name = f.combined ? "Combined" : (f.discipline || "Other")
      if (!groups.has(name)) groups.set(name, [])
      groups.get(name).push(f)
    })
    const rank = name => name === "Combined" ? -1 : name === "Other" ? 9998
      : (DISCIPLINE_ORDER.indexOf(name) === -1 ? 9000 : DISCIPLINE_ORDER.indexOf(name))
    const names = [...groups.keys()].sort((x, y) => rank(x) - rank(y) || x.localeCompare(y))

    // Pack boxes into rows.
    const rows = []
    let cur = [], used = 0
    const closeRow = () => { if (cur.length) rows.push(cur); cur = []; used = 0 }
    names.forEach(name => {
      const files = groups.get(name)
      let room = cur.length ? W - used - BOX_GAP : W
      let n = fits(room)
      if (cur.length && n < 1) { closeRow(); room = W; n = fits(W) }
      if (!cur.length && files.length > Math.max(n, 1)) {
        // Starts a row and overflows it: one full-width box holds them all.
        rows.push([{ name, files, header: true, full: true }])
        return
      }
      const take = Math.min(Math.max(n, 1), files.length)
      cur.push({ name, files: files.slice(0, take), header: true })
      used += (cur.length > 1 ? BOX_GAP : 0) + boxWidth(take)
      if (take < files.length) {
        closeRow()
        rows.push([{ name, files: files.slice(take), header: false, full: true }])
      }
    })
    closeRow()

    const boxHtml = seg => {
      const tint = DISCIPLINE_BOX[seg.name] || "border-gray-200 bg-gray-50"
      const pill = seg.name === "Combined" ? "bg-blue-100 text-blue-700" : (DISCIPLINE_COLORS[seg.name] || "bg-gray-100 text-gray-600")
      const total = groups.get(seg.name).length
      const header = seg.header
        ? `<div class="flex items-center gap-2 mb-2.5">
             <span class="inline-flex items-center rounded px-2 py-0.5 text-[13px] font-semibold ${pill}">${this._escapeHtml(seg.name)}</span>
             <span class="inline-flex items-center justify-center min-w-[22px] px-1.5 py-0.5 rounded-full text-[13px] leading-none bg-white/80 text-gray-500 border border-gray-200">${total}</span>
           </div>`
        : ""
      return `<div class="rounded-xl border ${tint} ${seg.full ? "w-full" : "shrink-0"}" style="padding:${PAD}px" data-grid-group="${this._escapeHtml(seg.name)}">
        ${header}
        <div class="flex flex-wrap" style="gap:${GAP}px">${seg.files.map(f => this._renderCard(f)).join("")}</div>
      </div>`
    }
    cont.innerHTML = rows.map(row =>
      `<div class="flex items-start" style="gap:${BOX_GAP}px">${row.map(boxHtml).join("")}</div>`
    ).join("")
  }

  // ── S2-14: pictures still being made ──────────────────────────────
  // Cards whose page-1 picture isn't ready yet ask for it every few seconds
  // (a fresh upload takes a moment to store and render), for about 2 minutes.

  _scheduleThumbPoll() {
    if (this.currentViewValue === "list" || !this.sheetThumbsUrlValue) return
    if (this._thumbPollTimer) return
    this._thumbPollTries ||= 0
    this._thumbPollTimer = setTimeout(() => { this._thumbPollTimer = null; this._pollThumbs() }, 4000)
  }

  _stopThumbPoll() {
    clearTimeout(this._thumbPollTimer)
    this._thumbPollTimer = null
    this._thumbPollTries = 0
  }

  async _pollThumbs() {
    if (!this.hasGridContainerTarget || this.currentViewValue === "list") return
    const pending = [...this.gridContainerTarget.querySelectorAll("[data-grid-card][data-thumb-pending]")].map(c => c.dataset.fileId)
    if (!pending.length || this._thumbPollTries >= 30) return
    this._thumbPollTries++
    try {
      const url = new URL(this.sheetThumbsUrlValue, window.location.origin)
      pending.slice(0, 200).forEach(id => url.searchParams.append("ids[]", id))
      const res = await fetch(url, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      Object.entries(data.thumbs || {}).forEach(([id, t]) => {
        const file = this._filesData[String(id)]
        if (!file || (!t.url && t.pending)) return
        if (t.url) file.thumb_url = t.url
        file.thumb_pending = false
        const card = this.gridContainerTarget.querySelector(`[data-grid-card][data-file-id="${id}"]`)
        if (!card) return
        card.removeAttribute("data-thumb-pending")
        const box = card.querySelector("[data-thumb-box]")
        if (box) box.innerHTML = this._cardThumbHtml(file)
      })
    } catch (e) {
      console.warn("[DrawingsSpecs] thumbnail poll:", e)
    }
    this._scheduleThumbPoll()
  }

  // ── Private: Discipline Tabs ──────────────────────────────────────

  _renderDisciplineTabs(counts) {
    if (!this.hasDisciplineMenuTarget || !counts) return

    this._disciplineCounts = counts

    this.disciplineMenuTarget.innerHTML = counts.map(disc => {
      const isActive = (this._activeDiscipline === null && disc.id === null) ||
                       String(disc.id) === String(this._activeDiscipline)

      return `<button type="button"
                      class="w-full flex items-center justify-between px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-gray-50 ${isActive ? 'text-brand font-semibold bg-brand/5' : 'text-gray-700'}"
                      data-action="click->drawings-specs#selectDiscipline"
                      data-discipline="${disc.id || ""}">
        <span>${this._escapeHtml(disc.name)}</span>
        <span class="inline-flex items-center justify-center min-w-[22px] px-1.5 py-0.5 rounded-full text-xs leading-none ${isActive ? 'bg-brand/15 text-brand font-bold' : 'bg-gray-100 text-gray-500'}">${disc.count}</span>
      </button>`
    }).join("")

    this._updateDisciplineLabel()
  }

  _updateDisciplineLabel() {
    if (!this.hasDisciplineLabelTarget || !this._disciplineCounts) return
    const active = this._disciplineCounts.find(d =>
      (this._activeDiscipline === null && d.id === null) ||
      String(d.id) === String(this._activeDiscipline)
    )
    if (active) {
      this.disciplineLabelTarget.innerHTML = `${this._escapeHtml(active.name)} <span class="inline-flex items-center justify-center min-w-[18px] px-1.5 py-0.5 rounded-full text-xs leading-none bg-brand/15 text-brand font-bold">${active.count}</span>`
    }
  }

  _updateDisciplineTabStyles() {
    if (!this.hasDisciplineMenuTarget) return
    this.disciplineMenuTarget.querySelectorAll("button").forEach(btn => {
      const disc = btn.dataset.discipline
      const isActive = (this._activeDiscipline === null && (disc === "null" || disc === "")) ||
                       String(disc) === String(this._activeDiscipline)
      if (isActive) {
        btn.classList.add("text-brand", "font-semibold", "bg-brand/5")
        btn.classList.remove("text-gray-700")
        btn.querySelector("span:last-child")?.classList.add("bg-brand/15", "text-brand", "font-bold")
        btn.querySelector("span:last-child")?.classList.remove("bg-gray-100", "text-gray-500")
      } else {
        btn.classList.remove("text-brand", "font-semibold", "bg-brand/5")
        btn.classList.add("text-gray-700")
        btn.querySelector("span:last-child")?.classList.remove("bg-brand/15", "text-brand", "font-bold")
        btn.querySelector("span:last-child")?.classList.add("bg-gray-100", "text-gray-500")
      }
    })
  }

  // ── Private: Drawing Set Filter ───────────────────────────────────

  // Sets whose files were all deleted stay in the database. Keep them out of
  // the filter chips and the export dropdown (the active filter stays visible).
  _setsWithFiles(sets, activeId = null) {
    return (sets || []).filter(s => s.count !== 0 || (activeId && String(s.id) === String(activeId)))
  }

  _populateDrawingSetFilter(sets) {
    if (!this.hasDrawingSetFilterTarget) return
    sets = this._setsWithFiles(sets, this._drawingSetFilter)
    const activeCls = "bg-brand/10 text-brand font-medium border-brand/30"
    const inactiveCls = "text-gray-500 hover:bg-gray-50 border-gray-200"
    const isAllActive = !this._drawingSetFilter
    let html = `<button type="button"
                        class="px-2.5 py-1 text-[13px] rounded-full transition-colors cursor-pointer border ${isAllActive ? activeCls : inactiveCls}"
                        data-action="click->drawings-specs#selectDrawingSet"
                        data-set-id="">All</button>`
    sets.forEach(s => {
      const isActive = this._drawingSetFilter === String(s.id)
      html += `<button type="button"
                       class="px-2.5 py-1 text-[13px] rounded-full transition-colors cursor-pointer border ${isActive ? activeCls : inactiveCls}"
                       data-action="click->drawings-specs#selectDrawingSet"
                       data-set-id="${s.id}">${this._escapeHtml(s.name)}</button>`
    })
    this.drawingSetFilterTarget.innerHTML = html
  }

  async _fetchDrawingSets() {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.drawingSetsUrlValue, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const sets = await res.json()
      this._drawingSetsCache = sets

      if (this.hasUploadSetSelectTarget) {
        let html = '<option value="">Select a drawing set...</option>'
        sets.forEach(s => {
          html += `<option value="${s.id}">${this._escapeHtml(s.name)}${s.issued_date ? ` (${s.issued_date})` : ""}</option>`
        })
        this.uploadSetSelectTarget.innerHTML = html
      }
    } catch (e) {
      console.error("[DrawingsSpecs] Failed to fetch drawing sets:", e)
    }
  }

  // ── Private: Detail Slide-Over ────────────────────────────────────

  _openDetail(id) {
    id = String(id)
    this._activeFileId = id
    this._modalOpen = true
    const file = this._filesData[id]

    // Update navigation buttons
    const idx = this._fileIds.indexOf(id)
    this.prevBtnTarget.disabled = idx <= 0
    this.nextBtnTarget.disabled = idx >= this._fileIds.length - 1

    // Show modal with animation
    this.detailModalTarget.classList.remove("hidden")
    this.detailLoadingTarget.classList.remove("hidden")
    this.detailPreviewTarget.innerHTML = ""
    this.detailMetaTarget.classList.add("hidden")
    if (this.hasDetailRevisionHistoryTarget) this.detailRevisionHistoryTarget.classList.add("hidden")
    if (this.hasDetailLinkedItemsTarget) this.detailLinkedItemsTarget.classList.add("hidden")

    requestAnimationFrame(() => {
      this.detailBackdropTarget.classList.add("opacity-100")
      this.detailBackdropTarget.classList.remove("opacity-0")
      this.detailPanelTarget.classList.remove("translate-x-full")
    })

    document.addEventListener("keydown", this._onKeydown)

    if (!file) {
      this.detailLoadingTarget.classList.add("hidden")
      this.detailPreviewTarget.innerHTML = '<p class="p-6 text-gray-400">File not found</p>'
      return
    }

    // Set title
    const titleText = file.sheet_number
      ? `${file.sheet_number}${file.title ? " — " + file.title : ""}`
      : file.file_name || "File"
    this.detailTitleTarget.querySelector("span").textContent = titleText
    this.detailDownloadBtnTarget.href = file.file_url || "#"

    this.detailLoadingTarget.classList.add("hidden")

    // Preview
    if (file.is_image && file.file_url) {
      this.detailPreviewTarget.innerHTML = `
        <div class="flex-1 flex items-center justify-center p-4 bg-gray-50">
          <img src="${file.file_url}" alt="${this._escapeHtml(file.file_name || "")}"
               class="max-w-full max-h-[70vh] object-contain rounded shadow-sm">
        </div>`
    } else if (file.is_pdf && file.file_url) {
      this.detailPreviewTarget.innerHTML = `
        <div class="flex-1 flex items-center justify-center p-8">
          <button type="button" class="flex flex-col items-center gap-3 px-8 py-6 rounded-xl border-2 border-dashed border-gray-300 hover:border-brand/50 hover:bg-brand/5 cursor-pointer transition-colors"
                  data-action="click->drawings-specs#openPdfViewer" data-file-id="${file.id}">
            ${this._fileTypeIcon("pdf", "w-12 h-12")}
            <span class="text-sm font-medium text-gray-600">Open PDF Viewer</span>
          </button>
        </div>`
    } else {
      this.detailPreviewTarget.innerHTML = `
        <div class="flex-1 flex items-center justify-center p-8">
          <div class="text-center">
            ${this._fileTypeIcon(file.extension, "w-16 h-16 mx-auto mb-3")}
            <p class="text-base font-medium text-gray-600 mb-1">${this._escapeHtml(file.file_name || "")}</p>
            <p class="text-sm text-gray-400">.${this._escapeHtml(file.extension || "file")} file</p>
            ${file.file_url ? `<a href="${file.file_url}" target="_blank" rel="noopener" class="inline-flex items-center gap-1.5 mt-4 px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg transition-colors">Download</a>` : ""}
          </div>
        </div>`
    }

    // Metadata
    const discBadge = file.discipline
      ? `<span class="inline-flex items-center rounded px-1.5 py-px text-[13px] font-medium ${DISCIPLINE_COLORS[file.discipline] || "bg-gray-100 text-gray-600"}">${this._escapeHtml(file.discipline)}</span>`
      : '<span class="text-gray-400">—</span>'

    this.detailMetaTarget.classList.remove("hidden")
    this.detailMetaTarget.innerHTML = `
      <div class="flex items-center justify-between">
        <span class="text-gray-400">Discipline</span>
        ${discBadge}
      </div>
      <div class="flex items-center justify-between">
        <span class="text-gray-400">Current Rev</span>
        <span class="inline-flex items-center gap-1.5">${this._escapeHtml(file.revision_number || "—")}${issueTagChip(file.issue_tag, file.drawing_set_name)}<span>${file.drawing_set_name ? ` · ${this._escapeHtml(file.drawing_set_name)}` : ""}${file.formatted_date ? ` · ${this._escapeHtml(file.formatted_date)}` : ""}</span></span>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-gray-400">Uploaded by</span>
        <span>${this._escapeHtml(file.uploader_name || "—")}</span>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-gray-400">File</span>
        <span class="truncate max-w-[300px]">${this._escapeHtml(file.file_name || "—")}</span>
      </div>`

    // Fetch revision history
    this._loadSheetHistory(id)
  }

  async _loadSheetHistory(id) {
    if (!this.sheetHistoryUrlTemplateValue) return
    const url = this.sheetHistoryUrlTemplateValue.replace("__ID__", id)
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(url, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const data = await res.json()

      // Render revision history
      if (data.revisions && data.revisions.length > 0 && this.hasDetailRevisionHistoryTarget) {
        this.detailRevisionHistoryTarget.classList.remove("hidden")
        this.detailRevisionHistoryTarget.innerHTML = `
          <h4 class="text-[13px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Revision History</h4>
          <div class="space-y-1.5">
            ${data.revisions.map(rev => `
              <div class="flex items-center gap-3 text-[13px] ${rev.is_current ? "" : "text-gray-400"}">
                <span class="w-3 h-3 rounded-full ${rev.is_current ? "bg-brand" : "border-2 border-gray-300"} shrink-0"></span>
                <span class="font-medium ${rev.is_current ? "text-gray-800" : ""}">Rev ${this._escapeHtml(rev.revision_number || "?")}</span>
                ${issueTagChip(rev.issue_tag, rev.drawing_set_name)}
                <span class="text-gray-400 truncate">${this._escapeHtml(rev.drawing_set_name || "")}</span>
                <span class="flex-1"></span>
                <span>${this._escapeHtml(rev.formatted_date || "")}</span>
                <span class="text-gray-400 truncate max-w-[80px]">${this._escapeHtml(rev.uploader_name || "")}</span>
                ${rev.file_url ? `<a href="${rev.file_url}" target="_blank" rel="noopener" class="text-brand hover:text-brand-dark text-[12px] font-medium">view</a>` : ""}
              </div>
            `).join("")}
          </div>`
      }

      // Render linked items
      if (data.linked_items && data.linked_items.length > 0 && this.hasDetailLinkedItemsTarget) {
        this.detailLinkedItemsTarget.classList.remove("hidden")
        this.detailLinkedItemsTarget.innerHTML = `
          <h4 class="text-[13px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Linked Action Items</h4>
          <div class="space-y-1.5">
            ${data.linked_items.map(item => `
              <div class="flex items-center gap-2 text-[13px]">
                <span class="font-medium text-gray-700">${this._escapeHtml(item.kind || "")}</span>
                <span class="text-gray-600 truncate flex-1">${this._escapeHtml(item.name || "")}</span>
                <span class="inline-flex items-center rounded px-1.5 py-px text-[11px] font-medium ${item.status === "open" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}">${this._escapeHtml(item.status || "")}</span>
              </div>
            `).join("")}
          </div>`
      }
    } catch (e) {
      console.error("[DrawingsSpecs] sheet_history failed:", e)
    }
  }

  // ── Private: Combined PDF Split ──────────────────────────────────────

  async _checkForCombinedPdfs() {
    // Find PDFs that haven't been split-checked yet
    const candidates = this._uploadFiles.filter(uf => {
      if (uf._splitChecked) return false
      const ext = uf.file.name.split(".").pop()?.toLowerCase()
      return ext === "pdf"
    })

    if (candidates.length === 0) {
      this._extractMetadataForNewFiles()
      return
    }

    candidates.forEach(uf => { uf._splitChecked = true })
    this._splitCancelled = false

    // Show AI banner with timer
    this.uploadAiStatusTarget.classList.remove("hidden")
    this.uploadAiStatusTarget.classList.add("flex")
    this._aiTimerStart = performance.now()
    clearInterval(this._aiTimerInterval)
    const updateBannerText = (msg) => {
      const secs = Math.round((performance.now() - this._aiTimerStart) / 1000)
      this.uploadAiStatusTextTarget.innerHTML = `<span class="font-bold ai-rainbow-text">ConsLog AI — ${msg}</span> <span data-ai-timer class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">${secs}s</span>`
    }
    updateBannerText("Scanning pages...")
    this._aiTimerInterval = setInterval(() => {
      const timerEl = this.uploadAiStatusTextTarget.querySelector("[data-ai-timer]")
      if (timerEl) timerEl.textContent = `${Math.round((performance.now() - this._aiTimerStart) / 1000)}s`
    }, 1000)
    const icon = this.uploadAiStatusTarget.querySelector("svg")
    if (icon) { icon.classList.add("ai-rainbow-icon"); icon.style.color = "" }

    let anySplit = false

    for (const uf of candidates) {
      if (this._splitCancelled) break

      try {
        const arrayBuffer = await uf.file.arrayBuffer()

        // Phase 1: Extract page headers (client-side, fast)
        const result = await extractPageHeaders(arrayBuffer, (progress) => {
          if (this._splitCancelled) return
          if (progress.phase === "scanning" && progress.current) {
            updateBannerText(`Scanning pages... ${progress.current} of ${progress.total}`)
          }
        }, 2) // minPages=2 for drawings (specs uses default 5)

        if (!result || this._splitCancelled) continue

        // Phase 2: Send page headers to LLM for analysis
        updateBannerText("Analyzing drawing set...")

        const token = document.querySelector('meta[name="csrf-token"]')?.content
        const res = await fetch(this.analyzeCombinedUrlValue, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
          credentials: "same-origin",
          body: JSON.stringify({ pages: result.pages })
        })
        const data = await res.json()
        const sheets = data.sheets || []
        // S2-15: spec sections inside an addendum, filed on the Specs page.
        const specSections = data.spec_sections || []

        // A spec book with no sheets in it: say so and point to Specs.
        if (data.document_type === "specifications" && sheets.length === 0) {
          console.log(`[DrawingsSpecs] ${uf.file.name} classified as specifications — showing warning`)
          clearInterval(this._aiTimerInterval)
          this.uploadAiStatusTarget.classList.add("hidden")
          this.uploadAiStatusTarget.classList.remove("flex")
          this.closeUploadModal()
          this._showWrongTypeModal()
          return
        }

        console.log(`[DrawingsSpecs] LLM found ${sheets.length} sheets:`, sheets.map(s => `${s.sheet_number} — ${s.title} (p${s.page})`))

        // Split a set of sheets — or an addendum with even one sheet and its
        // spec sections (S2-15).
        const splitIt = sheets.length >= 2 || (sheets.length >= 1 && specSections.length > 0)
        if (!splitIt || this._splitCancelled) continue

        // Phase 3: Split PDF progressively using LLM-identified sheets
        const totalSheets = sheets.length
        updateBannerText(`Splitting into ${totalSheets} sheets...`)

        const splitter = splitDrawingBySheets(uf.file, arrayBuffer, sheets, result.numPages, (progress) => {
          if (progress.phase === "splitting" && progress.current) {
            updateBannerText(`Splitting... ${progress.current} of ${progress.total} sheets`)
          }
        })

        let firstSheet = true
        let splitCount = 0
        const splitSheetNumbers = []
        const splitEntries = []

        for await (const sheet of splitter) {
          if (this._splitCancelled) break
          if (sheet === null) break

          if (firstSheet) {
            firstSheet = false
            anySplit = true
            this._uploadFiles = this._uploadFiles.filter(f => f.id !== uf.id)
            const origRow = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"]`)
            if (origRow) origRow.remove()
          }

          const id = Date.now() + Math.round(Math.random() * 1000)
          const entry = {
            id,
            file: sheet.file,
            sheetNumber: sheet.sheetNumber,
            title: sheet.title,
            discipline: sheet.discipline,
            // Page this sheet came off the combined set, carried through to
            // the server so markup can mirror back onto it (Larry 2026-08-21).
            pageNumber: sheet.pageNumber,
            _extracted: true,
            _splitChecked: true
          }
          this._uploadFiles.push(entry)
          splitEntries.push(entry)
          this._renderUploadSheetRow(id, sheet.file, {
            sheetNumber: sheet.sheetNumber,
            title: sheet.title,
            discipline: sheet.discipline
          })
          splitSheetNumbers.push(sheet.sheetNumber)
          splitCount++

          if (this.hasUploadSheetTableTarget) {
            this.uploadSheetTableTarget.classList.remove("hidden")
          }
        }

        // Batch check NEW/REV
        if (splitSheetNumbers.length > 0) {
          this._batchCheckSheets(splitSheetNumbers)
        }

        // S2-15: the addendum's spec sections, cut out the same way and
        // filed on the Specs page on upload. The bid form stays in the full PDF.
        let specCount = 0
        if (splitCount > 0 && specSections.length > 0 && !this._splitCancelled) {
          updateBannerText(`Filing ${specSections.length} spec section${specSections.length === 1 ? "" : "s"}...`)
          const specEntries = []
          for await (const sec of splitSpecBySections(uf.file, arrayBuffer, specSections, result.numPages)) {
            if (this._splitCancelled || sec === null) break
            const id = Date.now() + Math.round(Math.random() * 1000)
            const entry = {
              id,
              file: sec.file,
              sheetNumber: "",
              sectionNumber: sec.sectionNumber,
              title: sec.title,
              division: sec.division,
              _toSpecs: true,
              _extracted: true,
              _splitChecked: true
            }
            this._uploadFiles.push(entry)
            splitEntries.push(entry)
            specEntries.push(entry)
            this._renderSpecSectionRow(entry)
            specCount++
          }
          this._checkSpecSections(specEntries)
        }

        if (splitCount > 0) {
          this._splitStates.push({
            originalFile: uf.file,
            splitEntries,
            splitCount,
            specCount
          })
          clearInterval(this._aiTimerInterval)
          // ConsLog Builder: keep the combined original alongside the
          // extracted sheets (instead of the owner's either/or).
          if (this.builderValue) this._keepCombinedOriginal(uf.file)
          this._showSplitBannerWithSkipLink()
          const ic = this.uploadAiStatusTarget.querySelector(".ai-rainbow-icon")
          if (ic) { ic.classList.remove("ai-rainbow-icon"); ic.style.color = "#60a5ee" }
        }
      } catch (e) {
        console.error(`[DrawingsSpecs] PDF split failed for ${uf.file.name}:`, e)
      }
    }

    if (!anySplit) {
      clearInterval(this._aiTimerInterval)
      this.uploadAiStatusTarget.classList.add("hidden")
      this.uploadAiStatusTarget.classList.remove("flex")
    }

    this._suggestSetName()
    this._autoFillSetType()
    this._extractMetadataForNewFiles()
  }

  async _batchCheckSheets(sheetNumbers) {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.checkSheetsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ sheet_numbers: sheetNumbers, issued_date: this._uploadIssuedDate() })
      })
      const results = await res.json()

      results.forEach(result => {
        const uf = this._uploadFiles.find(f => f.sheetNumber === result.sheet_number)
        if (!uf) return
        const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uf.id}"]`)
        if (!row) return
        const sheetInput = row.querySelector(`input[data-upload-id="${uf.id}"]`)
        if (!sheetInput) return
        this._paintSheetCheck(uf, row, sheetInput, result)
      })
    } catch (e) {
      console.error("[DrawingsSpecs] Batch check sheets failed:", e)
    }
  }

  // The date of the set being uploaded, when the window knows it: a new
  // set's Received date, or the chosen existing set's. For the OLDER check.
  _uploadIssuedDate() {
    const isNew = !this.hasUploadSetModeSelectTarget || this.uploadSetModeSelectTarget.value === "new"
    if (isNew) return this.hasUploadSetDateInputTarget ? this.uploadSetDateInputTarget.value : ""
    const id = this.hasUploadSetSelectTarget ? this.uploadSetSelectTarget.value : ""
    const set = (this._drawingSetsCache || []).find(s => String(s.id) === String(id))
    return set?.issued_date || ""
  }

  // The REV / OLDER / NEW tag beside the file name, the amber tint on a sheet
  // that already exists, and the sheet's spelling as the log already has it
  // ("A101" typed → "A-101"). OLDER (S2-13): the set is dated before the
  // current version, so this one files under it instead of replacing it.
  _paintSheetCheck(uf, row, sheetInput, result) {
    row.querySelector("[data-sheet-label]")?.remove()
    const fileNameSpan = row.querySelector("td:first-child span")
    const tag = (text, color, title = "") =>
      fileNameSpan?.insertAdjacentHTML("afterend", `<span data-sheet-label class="text-[13px] font-bold ${color} ml-1.5 whitespace-nowrap" title="${this._escapeHtml(title)}">${text}</span>`)
    if (result && result.exists) {
      if (result.existing_sheet_number && result.existing_sheet_number !== uf.sheetNumber) {
        uf.sheetNumber = result.existing_sheet_number
        sheetInput.value = result.existing_sheet_number
      }
      sheetInput.classList.add("bg-amber-50", "border-amber-300")
      sheetInput.classList.remove("bg-blue-50", "border-blue-200")
      if (result.older) {
        sheetInput.title = `Dated before the current ${uf.sheetNumber} (Rev ${result.current_revision || "?"}) — files under it as an older version`
        tag("OLDER", "text-gray-500", sheetInput.title)
      } else {
        sheetInput.title = `Revises ${uf.sheetNumber} (currently Rev ${result.current_revision || "?"})`
        tag("REV", "text-amber-500", sheetInput.title)
      }
    } else {
      sheetInput.title = "New sheet"
      sheetInput.classList.remove("bg-amber-50", "border-amber-300")
      tag("NEW", "text-emerald-500")
    }
    // No markups to copy onto an older version — the current one keeps them.
    this._showCopyMarkupsBox(uf.id, result && !result.older ? result : null)
  }

  // ── Private: Upload Row Render ────────────────────────────────────

  _renderUploadSheetRow(id, file, prefilled) {
    // Deduplicate discipline options
    const uniqueDiscs = [...new Set(Object.values(DISCIPLINE_PREFIXES))]
    const disciplineOptions = uniqueDiscs.map(name =>
      `<option value="${name}" ${prefilled?.discipline === name ? "selected" : ""}>${name}</option>`
    ).join("")

    const preClass = prefilled ? "bg-blue-50 border-blue-200" : "border-gray-300"

    this.uploadSheetRowsTarget.insertAdjacentHTML("beforeend", `
      <tr data-upload-row="${id}" class="border-b border-gray-100">
        <td class="py-1.5 pr-3">
          <div class="flex items-center gap-1 min-w-0">
            <span class="text-[13px] text-gray-700 truncate" title="${this._escapeHtml(file.name)}">${this._escapeHtml(file.name)}</span>
          </div>
          <div class="relative h-1 rounded-full mt-1 hidden" data-progress-track>
            <div data-progress-bar class="h-full bg-brand rounded-full transition-all duration-200" style="width: 0%"></div>
          </div>
        </td>
        <td class="py-1.5 px-3">
          <input type="text" class="w-full text-[13px] rounded-lg border ${preClass} focus:border-brand focus:ring-0 py-1 px-2 font-mono"
                 placeholder="A-101"
                 value="${prefilled?.sheetNumber ? this._escapeHtml(prefilled.sheetNumber) : ""}"
                 data-upload-id="${id}"
                 data-action="input->drawings-specs#uploadSheetNumberChanged blur->drawings-specs#uploadSheetNumberBlur">
        </td>
        <td class="py-1.5 px-3">
          <input type="text" data-title-input class="w-full text-[13px] rounded-lg border ${preClass} focus:border-brand focus:ring-0 py-1 px-2"
                 placeholder="Drawing title"
                 value="${prefilled?.title ? this._escapeHtml(prefilled.title) : ""}"
                 data-upload-id="${id}"
                 data-action="input->drawings-specs#uploadTitleChanged">
        </td>
        <td class="py-1.5 px-3">
          <select data-discipline-input class="w-full text-[13px] rounded-lg border ${preClass} focus:border-brand focus:ring-0 py-1 px-2"
                  data-upload-id="${id}"
                  data-action="change->drawings-specs#uploadDisciplineChanged">
            <option value="">—</option>
            ${disciplineOptions}
          </select>
        </td>
        <td class="py-1.5 pl-3">
          <button type="button" class="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                  data-upload-id="${id}" data-action="click->drawings-specs#removeUploadFile">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </td>
      </tr>`)
  }

  _updateUploadBtnText() {
    const count = this._uploadFiles.length
    const hasCombined = this.builderValue && this._uploadFiles.some(uf => uf._isCombined)
    // The split-button dropdown only applies when a combined was kept.
    if (this.hasUploadMenuBtnTarget) this.uploadMenuBtnTarget.classList.toggle("hidden", !hasCombined)
    if (!hasCombined) this._closeUploadMenu()
    this.uploadSubmitBtnTarget.classList.remove("rounded-lg", "rounded-l-lg")
    this.uploadSubmitBtnTarget.classList.add(hasCombined ? "rounded-l-lg" : "rounded-lg")
    // S2-15: spec sections split off an addendum go to the Specs page.
    const specs = this._uploadFiles.filter(uf => uf._toSpecs).length
    const specsText = specs ? ` + ${specs} Spec Section${specs === 1 ? "" : "s"}` : ""
    if (hasCombined) {
      const extracted = this._uploadFiles.filter(uf => !uf._isCombined && !uf._toSpecs).length
      this.uploadSubmitBtnTarget.textContent = `Upload Combined & Extract ${extracted} Sheet${extracted === 1 ? "" : "s"}${specsText}`
      return
    }
    const sheetCount = count - specs
    this.uploadSubmitBtnTarget.textContent = count > 0 ? `Upload ${sheetCount} sheet${sheetCount === 1 ? "" : "s"}${specsText}` : "Upload"
  }

  // ── Skip / Undo splitting ──────────────────────────────────────

  // ConsLog Builder: keep the original combined PDF in the upload queue
  // alongside the split sheets (marked `_isCombined` so it registers as
  // the combined set record). De-duped per original file.
  _keepCombinedOriginal(file) {
    if (this._uploadFiles.some(uf => uf._isCombined && uf.file === file)) return
    const origId = Date.now() + Math.round(Math.random() * 1000)
    const titleFromName = file.name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim()
    this._uploadFiles.push({
      id: origId,
      file,
      sheetNumber: "Combined",
      title: titleFromName,
      discipline: "",
      _extracted: true,
      _splitChecked: true,
      _isCombined: true
    })
    this._renderCombinedFileRow(origId, file)
  }

  _showSplitBannerWithSkipLink() {
    const totalSheets = this._splitStates.reduce((sum, s) => sum + s.splitCount, 0)
    const totalSpecs = this._splitStates.reduce((sum, s) => sum + (s.specCount || 0), 0)
    const specsText = totalSpecs ? ` · ${totalSpecs} spec section${totalSpecs > 1 ? "s" : ""} to Specs` : ""
    const currentTimer = this.uploadAiStatusTextTarget.querySelector("[data-ai-timer]")
    const timerHtml = currentTimer ? currentTimer.outerHTML : ""

    // Builder always keeps the combined + the sheets, so there's no
    // either/or "Upload as Combined" skip button.
    if (this.builderValue) {
      this.uploadAiStatusTextTarget.innerHTML =
        `<span style="color: #60a5ee; font-weight: bold;">ConsLog AI — ${totalSheets} sheet${totalSheets > 1 ? "s" : ""} extracted${specsText} · combined kept</span> ${timerHtml}`
      return
    }

    this.uploadAiStatusTextTarget.innerHTML =
      `<span style="color: #60a5ee; font-weight: bold;">ConsLog AI — ${totalSheets} sheet${totalSheets > 1 ? "s" : ""} extracted${specsText}</span> ${timerHtml}` +
      `<button type="button" class="ml-2 px-2.5 py-0.5 text-[13px] font-medium text-gray-600 bg-white border border-gray-300 rounded-full hover:bg-gray-50 hover:text-gray-800 cursor-pointer transition-colors" data-action="click->drawings-specs#skipSplitting">Upload as Combined</button>`
  }

  skipSplitting(event) {
    if (event) event.preventDefault()
    if (!this._splitStates || this._splitStates.length === 0) return

    for (const state of this._splitStates) {
      // Remove all split rows from DOM and _uploadFiles
      for (const entry of state.splitEntries) {
        this._removeUploadRow(entry.id)
        this._uploadFiles = this._uploadFiles.filter(f => f.id !== entry.id)
      }

      // Re-add original combined file as a single upload
      const origId = Date.now() + Math.round(Math.random() * 1000)
      state._restoredId = origId
      const titleFromName = state.originalFile.name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim()
      this._uploadFiles.push({
        id: origId,
        file: state.originalFile,
        sheetNumber: "Combined",
        title: titleFromName,
        discipline: "",
        _extracted: true,
        _splitChecked: true
      })
      this._renderCombinedFileRow(origId, state.originalFile)
    }

    // Update banner with undo link
    const totalSheets = this._splitStates.reduce((sum, s) => sum + s.splitCount, 0)
    const fileCount = this._splitStates.length
    const fileLabel = fileCount > 1 ? `${fileCount} files` : "single file"

    this.uploadAiStatusTextTarget.innerHTML =
      `<span style="color: #60a5ee; font-weight: bold;">ConsLog AI — Uploaded as ${fileLabel}</span>` +
      `<button type="button" class="ml-2 px-2.5 py-0.5 text-[13px] font-medium text-brand bg-white border border-brand/30 rounded-full hover:bg-brand/5 hover:border-brand/50 cursor-pointer transition-colors" data-action="click->drawings-specs#undoSkipSplitting">Split into ${totalSheets} sheet${totalSheets > 1 ? "s" : ""}</button>`

    if (this.hasUploadSheetTableTarget) this.uploadSheetTableTarget.classList.remove("hidden")
    this._updateUploadBtnText()
    this.uploadSubmitBtnTarget.disabled = false
  }

  undoSkipSplitting(event) {
    if (event) event.preventDefault()
    if (!this._splitStates || this._splitStates.length === 0) return

    for (const state of this._splitStates) {
      // Remove the single-file row
      if (state._restoredId) {
        const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${state._restoredId}"]`)
        if (row) row.remove()
        this._uploadFiles = this._uploadFiles.filter(f => f.id !== state._restoredId)
      }

      // Re-add all split entries and re-render rows
      const sheetNumbers = []
      for (const entry of state.splitEntries) {
        this._uploadFiles.push(entry)
        if (entry._toSpecs) { this._renderSpecSectionRow(entry); continue }
        this._renderUploadSheetRow(entry.id, entry.file, {
          sheetNumber: entry.sheetNumber,
          title: entry.title,
          discipline: entry.discipline
        })
        sheetNumbers.push(entry.sheetNumber)
      }

      // Re-run batch check for NEW/REV styling
      if (sheetNumbers.length > 0) {
        this._batchCheckSheets(sheetNumbers)
      }
    }

    // Restore banner with skip link
    this._showSplitBannerWithSkipLink()
    this._updateUploadBtnText()
  }

  _renderCombinedFileRow(id, file) {
    const titleFromName = file.name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim()
    this.uploadSheetRowsTarget.insertAdjacentHTML("beforeend", `
      <tr data-upload-row="${id}" class="border-b border-gray-100">
        <td class="py-1.5 pr-3">
          <div class="flex items-center gap-1 min-w-0">
            <span class="text-[13px] text-gray-700 truncate" title="${this._escapeHtml(file.name)}">${this._escapeHtml(file.name)}</span>
          </div>
          <div class="relative h-1 rounded-full mt-1 hidden" data-progress-track>
            <div data-progress-bar class="h-full bg-brand rounded-full transition-all duration-200" style="width: 0%"></div>
          </div>
        </td>
        <td class="py-1.5 px-3" colspan="2">
          <input type="text" data-title-input class="w-full text-[13px] rounded-lg border border-gray-300 focus:border-brand focus:ring-0 py-1 px-2"
                 placeholder="Drawing title"
                 value="${this._escapeHtml(titleFromName)}"
                 data-upload-id="${id}"
                 data-action="input->drawings-specs#uploadTitleChanged">
        </td>
        <td class="py-1.5 px-3"></td>
        <td class="py-1.5 pl-3">
          <button type="button" class="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors"
                  data-upload-id="${id}" data-action="click->drawings-specs#removeUploadFile">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </td>
      </tr>`)
  }

  // K7-21: the set's Type + Number follow its name ("PR 5 – Kitchen" → PR, 5;
  // "Floor plans" → Just a new version) until they pick a type themselves.
  setNameTyped() {
    this._autoFillSetType()
  }

  setTypePicked() {
    this._setTypeTouched = true
  }

  _autoFillSetType() {
    if (this._setTypeTouched || !this.hasUploadSetNameInputTarget || !this.hasUploadSetTypeSelectTarget) return
    const { type, number } = guessIssueFromName(this.uploadSetNameInputTarget.value)
    this.uploadSetTypeSelectTarget.value = type || ""
    if (this.hasUploadSetNumberInputTarget) this.uploadSetNumberInputTarget.value = number || ""
  }

  _suggestSetName() {
    if (!this.hasUploadSetNameInputTarget) return
    // Only suggest if the user hasn't typed anything
    if (this.uploadSetNameInputTarget.value.trim()) return
    if (this._uploadFiles.length === 0) return

    // Strip extensions and find longest common prefix
    const names = this._uploadFiles.map(uf => uf.file.name.replace(/\.[^.]+$/, ""))
    if (names.length === 1) {
      this.uploadSetNameInputTarget.value = names[0]
      return
    }

    let prefix = names[0]
    for (let i = 1; i < names.length; i++) {
      while (names[i].indexOf(prefix) !== 0 && prefix.length > 0) {
        prefix = prefix.substring(0, prefix.length - 1)
      }
    }
    // Clean up trailing separators
    prefix = prefix.replace(/[-_\s.]+$/, "")
    if (prefix.length >= 3) {
      this.uploadSetNameInputTarget.value = prefix
    }
  }

  async _extractMetadataForNewFiles() {
    // Find PDF files that haven't been extracted yet
    const pdfFiles = this._uploadFiles.filter(uf => {
      if (uf._extracted) return false
      const ext = uf.file.name.split(".").pop()?.toLowerCase()
      return ext === "pdf"
    })
    if (pdfFiles.length === 0) {
      this._updateUploadBtnText()
      this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
      return
    }

    // Mark as in-progress so we don't re-extract
    pdfFiles.forEach(uf => { uf._extracted = true })

    // Show AI status banner with elapsed timer
    this.uploadAiStatusTarget.classList.remove("hidden")
    this.uploadAiStatusTarget.classList.add("flex")
    this._aiTimerStart = performance.now()
    this.uploadAiStatusTextTarget.innerHTML = `<span class="font-bold ai-rainbow-text">ConsLog AI — Analyzing Drawings</span> <span data-ai-timer class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">0s</span>`
    this._aiTimerInterval = setInterval(() => {
      const secs = Math.round((performance.now() - this._aiTimerStart) / 1000)
      const timerEl = this.uploadAiStatusTextTarget.querySelector("[data-ai-timer]")
      if (timerEl) timerEl.textContent = `${secs}s`
    }, 1000)

    // Process each PDF end-to-end independently: extract text/images → call server → apply result
    // Each file runs as its own promise so fast files populate immediately
    const MAX_PDF_BYTES = 250 * 1024 * 1024
    const MAX_TEXT_PAGES = 20
    const MAX_IMAGE_PAGES = 5
    const extractUrl = this.extractMetadataUrlValue
    let completed = 0
    const total = pdfFiles.length

    const promises = pdfFiles.map(async (uf) => {
      const fileStart = performance.now()
      try {
        if (uf.file.size > MAX_PDF_BYTES) {
          console.log(`[DrawingsSpecs] Skipping ${uf.file.name} — too large (${(uf.file.size / 1024 / 1024).toFixed(1)}MB)`)
          return
        }

        console.log(`[DrawingsSpecs] Processing ${uf.file.name} (${(uf.file.size / 1024).toFixed(0)}KB)...`)

        // Client-side: extract text or render images
        const arrayBuffer = await uf.file.arrayBuffer()
        const t1 = performance.now()
        const { pageTexts } = await extractPdfText(arrayBuffer.slice(0), MAX_TEXT_PAGES)
        console.log(`[DrawingsSpecs] Text extraction took ${Math.round(performance.now() - t1)}ms for ${uf.file.name} (${pageTexts.length} pages, ${pageTexts.reduce((s, t) => s + t.length, 0)} chars)`)

        const allText = pageTexts.map((t, i) => `--- PAGE ${i + 1} ---\n${t}`).join("\n\n")
        const hasText = pageTexts.some(t => t.trim().length > 50)

        let mode, content
        if (hasText) {
          mode = "text"
          content = allText
          console.log(`[DrawingsSpecs] Using text mode for ${uf.file.name} (${(content.length / 1024).toFixed(1)}KB payload)`)
        } else {
          const t2 = performance.now()
          console.log(`[DrawingsSpecs] No embedded text in ${uf.file.name}, falling back to JPEG rendering...`)
          const { images } = await renderPdfPagesToJpegs(arrayBuffer, MAX_IMAGE_PAGES)
          const totalImageKB = images.reduce((s, img) => s + img.length, 0) / 1024
          console.log(`[DrawingsSpecs] JPEG rendering took ${Math.round(performance.now() - t2)}ms for ${uf.file.name} (${images.length} pages, ${totalImageKB.toFixed(0)}KB base64)`)
          mode = "images"
          content = images
        }

        // Server-side: call Gemini via Rails proxy
        const t0 = performance.now()
        const result = await callExtractMetadata(extractUrl, mode, content, uf.file.name)
        console.log(`[DrawingsSpecs] extract_metadata (${mode}) returned in ${Math.round(performance.now() - t0)}ms for ${uf.file.name}:`, result)
        console.log(`[DrawingsSpecs] Total pipeline: ${Math.round(performance.now() - fileStart)}ms for ${uf.file.name}`)

        // Store extracted values
        if (result.sheet_number) uf.sheetNumber = result.sheet_number
        if (result.title) uf.title = result.title
        if (result.discipline) uf.discipline = result.discipline
      } catch (e) {
        console.error(`[DrawingsSpecs] AI extraction failed for ${uf.file.name} after ${Math.round(performance.now() - fileStart)}ms:`, e)
      } finally {
        // Render row with extracted data (or empty fields if extraction failed)
        const prefilled = (uf.sheetNumber || uf.title || uf.discipline)
          ? { sheetNumber: uf.sheetNumber, title: uf.title, discipline: uf.discipline }
          : null
        this._renderUploadSheetRow(uf.id, uf.file, prefilled)
        if (this.hasUploadSheetTableTarget) this.uploadSheetTableTarget.classList.remove("hidden")
        if (uf.sheetNumber) this._checkSheetAfterExtract(uf.id, uf.sheetNumber)

        completed++
        if (completed === total) {
          clearInterval(this._aiTimerInterval)
          const totalSecs = Math.round((performance.now() - this._aiTimerStart) / 1000)
          this.uploadAiStatusTextTarget.innerHTML = `<span style="color: #60a5ee; font-weight: bold;">ConsLog AI — Values Extracted</span> <span class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">${totalSecs}s</span>`
          const icon = this.uploadAiStatusTarget.querySelector(".ai-rainbow-icon")
          if (icon) { icon.classList.remove("ai-rainbow-icon"); icon.style.color = "#60a5ee" }

          // Restore skip link if any files were split in this batch
          if (this._splitStates && this._splitStates.length > 0) {
            this._showSplitBannerWithSkipLink()
          }
        }
      }
    })

    await Promise.allSettled(promises)
    // Re-enable upload button after AI processing
    this._updateUploadBtnText()
    this.uploadSubmitBtnTarget.disabled = this._uploadFiles.length === 0
  }

  async _renderPdfPagesAsImages(file, maxPages) {
    const arrayBuffer = await file.arrayBuffer()
    pdfjsLib.GlobalWorkerOptions.workerSrc = "/assets/pdf.worker.js"
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
    const numPages = Math.min(pdf.numPages, maxPages)
    const images = []

    for (let i = 1; i <= numPages; i++) {
      const page = await pdf.getPage(i)
      // Render at 1.5x scale for readability while keeping size reasonable
      const viewport = page.getViewport({ scale: 1.5 })
      const canvas = document.createElement("canvas")
      canvas.width = viewport.width
      canvas.height = viewport.height
      const ctx = canvas.getContext("2d")
      await page.render({ canvasContext: ctx, viewport }).promise
      // Convert to JPEG at 70% quality — keeps each page around 100-200KB
      const dataUrl = canvas.toDataURL("image/jpeg", 0.7)
      const base64 = dataUrl.split(",")[1]
      images.push(base64)
    }

    return images
  }

  async _checkSheetAfterExtract(uploadId, sheetNumber) {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.checkSheetsUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ sheet_numbers: [sheetNumber], issued_date: this._uploadIssuedDate() })
      })
      const results = await res.json()
      const result = results[0]
      const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${uploadId}"]`)
      if (!row) return
      const sheetInput = row.querySelector('input[data-upload-id="' + uploadId + '"]')
      if (!sheetInput) return
      const uf = this._uploadFiles.find(f => String(f.id) === String(uploadId))
      if (uf) this._paintSheetCheck(uf, row, sheetInput, result)
    } catch (e) {
      // Silently fail — the hint already shows AI-suggested
    }
  }

  // K7-23 (Larry 2026-10-06): under a row that revises a sheet whose current
  // version has markups, an unticked box to start the new version with them.
  // No markups on that version (or a new sheet) → no box.
  _showCopyMarkupsBox(id, result) {
    const uf = this._uploadFiles.find(f => f.id === id)
    const row = this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${id}"]`)
    const box = this.uploadSheetRowsTarget.querySelector(`[data-copy-markups-row="${id}"]`)
    if (!uf || !row || !result?.exists || !result?.has_markups) {
      box?.remove()
      row?.classList.add("border-b")
      if (uf) uf.copyMarkups = false
      return
    }
    if (box) return
    row.classList.remove("border-b")
    row.insertAdjacentHTML("afterend", `
      <tr data-copy-markups-row="${id}" class="border-b border-gray-100">
        <td colspan="5" class="pb-2 pr-3">
          <label class="inline-flex items-center gap-2 text-[13px] text-gray-600 cursor-pointer select-none">
            <input type="checkbox" class="rounded border-gray-300 text-brand focus:ring-0 cursor-pointer"
                   data-upload-id="${id}" ${uf.copyMarkups ? "checked" : ""}
                   data-action="change->drawings-specs#toggleCopyMarkups">
            ${this._escapeHtml(this.copyMarkupsLabelValue)}
          </label>
        </td>
      </tr>`)
  }

  toggleCopyMarkups(event) {
    const uf = this._uploadFiles.find(f => f.id === parseInt(event.currentTarget.dataset.uploadId))
    if (uf) uf.copyMarkups = event.currentTarget.checked
  }

  _removeUploadRow(id) {
    this.uploadSheetRowsTarget.querySelector(`[data-upload-row="${id}"]`)?.remove()
    this.uploadSheetRowsTarget.querySelector(`[data-copy-markups-row="${id}"]`)?.remove()
  }

  // ── Private: Helpers ──────────────────────────────────────────────

  _removeFileFromDOM(id) {
    id = String(id)
    const row = this.tableBodyTarget.querySelector(`tr[data-file-id="${id}"]`)
    if (row) row.remove()
    this._fileIds = this._fileIds.filter(fid => fid !== id)
    delete this._filesData[id]
    this._selectedIds.delete(id)
  }

  // Sync the sidebar "Drawings" nav counter (hidden at 0).
  _updateNavCount(n) {
    if (n == null) return
    const el = document.querySelector('[data-nav-count="drawings"]')
    if (!el) return
    el.textContent = n
    el.classList.toggle("hidden", Number(n) === 0)
  }

  _updateBulkBar() {
    if (!this.hasBulkCountTarget) return
    this.bulkCountTarget.textContent = `${this._selectedIds.size} selected`
    const styleBtn = (btn, ok, reason) => {
      btn.classList.toggle("opacity-40", !ok)
      btn.classList.toggle("cursor-not-allowed", !ok)
      btn.classList.toggle("cursor-pointer", ok)
      btn.classList.toggle("hover:bg-brand/5", ok)
      if (ok) btn.removeAttribute("data-tip")
      else btn.setAttribute("data-tip", reason)
    }
    if (this.hasCompareBtnTarget) {
      const { ok, reason } = this._compareEligibility()
      styleBtn(this.compareBtnTarget, ok, reason)
    }
  }

  // ── Compare choice modal (Phelipe 2026-08-01): Split vs Overlay ──────

  openCompareChoice() {
    if (!this._compareEligibility().ok) return
    // The choice popup only appears when there IS a choice (Phelipe
    // 2026-08-01): overlay needs two versions of the SAME sheet, so with
    // two different sheets selected we skip straight to split.
    if (!this._overlayEligibility().ok) return this.compareSelected()
    this.compareChoiceModalTarget.classList.remove("hidden")
  }

  closeCompareChoice() {
    if (this.hasCompareChoiceModalTarget) this.compareChoiceModalTarget.classList.add("hidden")
  }

  chooseSplitCompare() {
    this.closeCompareChoice()
    this.compareSelected()
  }

  chooseOverlayCompare() {
    if (!this._overlayEligibility().ok) return
    this.closeCompareChoice()
    this.compareOverlaySelected()
  }

  // Overlay needs the split rules PLUS both picks being versions of the
  // SAME sheet — overlaying two different sheets is meaningless.
  _overlayEligibility() {
    const base = this._compareEligibility()
    if (!base.ok) return base
    const [a, b] = [...this._selectedIds].map(id => (this._filesData || {})[id])
    const sameSheet = a.sheet_number && b.sheet_number && a.sheet_number === b.sheet_number
    if (!sameSheet) return { ok: false, reason: "Overlay works on two versions of the SAME sheet." }
    return { ok: true, reason: "" }
  }

  // Compare requires exactly two single-page sheet PDFs (the multi-page
  // Combined file can't be compared). Returns { ok, reason } so the button
  // can grey out + show a tooltip explaining the criteria.
  _compareEligibility() {
    const files = [...this._selectedIds].map(id => (this._filesData || {})[id]).filter(Boolean)
    if (files.length !== 2) return { ok: false, reason: "Select exactly 2 sheets to compare." }
    if (!files.every(f => f.is_pdf)) return { ok: false, reason: "Both selected sheets must be PDFs." }
    if (files.some(f => f.combined)) return { ok: false, reason: "The Combined file can't be compared — pick 2 single-page sheets." }
    return { ok: true, reason: "" }
  }

  // Whether this sheet has an older version we know about — either nested
  // under it (default view) or elsewhere in the loaded rows (historical view).
  _hasPreviousVersion(file) {
    if (!file || file.combined) return false
    if ((file.versions || []).length > 0) return true
    const key = file.sheet_number
    if (!key) return false
    const rev = parseInt(file.revision_number, 10) || 0
    return Object.values(this._filesData || {}).some(o =>
      o && String(o.id) !== String(file.id) && !o.combined &&
      o.sheet_number === key && ((parseInt(o.revision_number, 10) || 0) < rev)
    )
  }

  // Open the Apryse side-by-side compare on the two selected PDFs.
  // The OLDER version goes on the left, the newer on the right, so the
  // overlay diff reads "before → after" left-to-right.
  compareSelected() {
    if (!this._compareEligibility().ok) return
    const [a, b] = [...this._selectedIds]
    const [left, right] = this._olderFirst(a, b)
    this._dispatchCompare(left, right)
  }

  // "Compare (Overlay)" from the list toolbar (Phelipe 2026-08-01): open the
  // newer version in the viewer (that's also where closing the overlay
  // lands you), then build the red/blue blend over both selections.
  async compareOverlaySelected() {
    if (!this._overlayEligibility().ok) return
    const [a, b] = [...this._selectedIds]
    const [olderId, newerId] = this._olderFirst(a, b)
    const viewer = this.element.querySelector("[data-controller='action-item-pdf-viewer']")
    const viewerCtrl = viewer && this.application.getControllerForElementAndIdentifier(viewer, "action-item-pdf-viewer")
    if (!viewerCtrl || typeof viewerCtrl.openOverlayCompare !== "function") return

    this._openInViewer(newerId)
    const ready = await this._waitForViewerReady(viewerCtrl)
    if (!ready) return

    const data = this._filesData || {}
    const revOf = (id) => {
      const f = data[String(id)] || {}
      return f.revision_number != null ? `Rev ${f.revision_number}${f.issue_tag?.label ? ` · ${f.issue_tag.label}` : ""}` : ""
    }
    const f = data[String(newerId)] || {}
    const base = [f.sheet_number, f.title].filter(Boolean).join(" — ") || f.file_name || "Sheet"
    const title = `${base} · Overlay: ${revOf(olderId) || "older"} (red) vs ${revOf(newerId) || "newer"} (blue)`
    const urlFor = (id) => this.pdfViewerBaseUrlValue.replace(/\/0\//, `/${id}/`)
    viewerCtrl.openOverlayCompare({ oldUrl: urlFor(olderId), newUrl: urlFor(newerId), title })
  }

  // The overlay needs the viewer's engine up and the sheet in — poll until
  // the instance reports document-ready (45s ceiling).
  _waitForViewerReady(viewerCtrl) {
    return new Promise((resolve) => {
      const t0 = Date.now()
      const poll = setInterval(() => {
        if (viewerCtrl._documentReady && viewerCtrl._instance) { clearInterval(poll); resolve(true) }
        else if (Date.now() - t0 > 45000) { clearInterval(poll); resolve(false) }
      }, 300)
    })
  }

  // Order two sheet ids so the OLDER one is on the left ("before → after").
  // Rank: superseded older than current, then lower revision number, then
  // earlier created_at — whichever distinguishes them.
  _olderFirst(a, b) {
    const data = this._filesData || {}
    const rank = (id) => {
      const f = data[id] || {}
      const statusRank = f.status === "current" ? 1 : 0 // current is newer
      const rev = parseInt(f.revision_number, 10)
      const created = Date.parse(f.created_at) || 0
      return [statusRank, isNaN(rev) ? 0 : rev, created]
    }
    const [ra, rb] = [rank(a), rank(b)]
    const aOlder = ra[0] !== rb[0] ? ra[0] < rb[0]
      : ra[1] !== rb[1] ? ra[1] < rb[1]
      : ra[2] <= rb[2]
    return aOlder ? [a, b] : [b, a]
  }

  _dispatchCompare(left, right) {
    window.dispatchEvent(new CustomEvent("pdf-compare:open", {
      detail: { leftVersionId: left, rightVersionId: right }
    }))
  }

  // One-click "compare this sheet to its latest previous version".
  compareToPrevious(event) {
    if (event) event.stopPropagation()
    const id = event.currentTarget.dataset.fileId
    if (!id) return
    this._dispatchCompare(id, "previous")
  }

  // ── In-viewer compare picker ──────────────────────────────────────
  // Opened by the "Compare" button inside the PDF viewer. Lets the user
  // either diff against the previous revision or pick any other sheet.

  async openComparePicker(event) {
    const id = String(event?.detail?.fileId || "")
    const file = this._filesData?.[id]
    if (!id) return
    this._compareSourceId = id
    this._compareVersions = []
    // "split" = side-by-side (existing); "overlay" = Bluebeam-style stacked
    // tint view (old red / new blue), same-sheet versions only.
    this._compareMode = event?.detail?.mode === "overlay" ? "overlay" : "split"

    // Remember the ERB's default "no previous version" text once, so the
    // transient "Loading…" state can be swapped back afterwards.
    if (this.hasComparePickerEmptyTarget && this._comparePickerEmptyDefault == null) {
      this._comparePickerEmptyDefault = this.comparePickerEmptyTarget.textContent.trim()
    }

    if (this.hasComparePickerTitleTarget) {
      const label = file
        ? ([file.sheet_number, file.title].filter(Boolean).join(" — ") || file.file_name)
        : "sheet"
      this.comparePickerTitleTarget.textContent =
        `${this._compareMode === "overlay" ? "Overlay" : "Compare"} ${label}`
    }

    // Show the modal immediately, then load this sheet's version history.
    this._setComparePickerSections({ loading: true })
    this.comparePickerModalTarget.classList.remove("hidden")

    let versions = []
    try {
      const url = this.sheetHistoryUrlTemplateValue.replace("__ID__", id)
      const resp = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      const data = await resp.json()
      versions = Array.isArray(data.revisions) ? data.revisions : []
    } catch (_) { versions = [] }

    // Modal may have been closed while loading.
    if (this._compareSourceId !== id) return
    this._compareVersions = versions
    this._renderComparePicker(versions)
  }

  closeComparePicker() {
    if (this.hasComparePickerModalTarget) this.comparePickerModalTarget.classList.add("hidden")
    this._compareSourceId = null
    this._compareVersions = []
  }

  compareWithPrevious() {
    const id = this._compareSourceId
    if (!id) return
    this._launchCompare(id, "previous")
  }

  compareWithPicked(event) {
    const otherId = String(event.currentTarget.dataset.fileId || "")
    const id = this._compareSourceId
    if (!id || !otherId) return
    const [left, right] = this._orderVersions(id, otherId)
    this._launchCompare(left, right)
  }

  // Close the picker and open the side-by-side compare ON TOP of the viewer,
  // so closing the compare lands back on the sheet. It used to close the
  // viewer first, and that close steps the browser back a page, which made
  // Turbo restore the page and wipe out the compare too (K7-20, Larry on
  // C8). Overlay mode stays INSIDE the viewer — the blended diff loads there.
  _launchCompare(left, right) {
    if (this._compareMode === "overlay") return this._launchOverlay(left, right)
    this.closeComparePicker()
    this._dispatchCompare(left, right)
  }

  // Overlay two versions of the SAME sheet (Phelipe 2026-08-01): resolve
  // which is old vs new, then hand both to the viewer's overlay builder.
  _launchOverlay(left, right) {
    const vs = this._compareVersions || []
    let oldId, newId
    if (String(right) === "previous") {
      newId = String(left)
      const idx = vs.findIndex(v => String(v.id) === newId)
      const prev = (idx >= 0 && vs[idx + 1]) || vs.find(v => String(v.id) !== newId)
      if (!prev) { this.closeComparePicker(); return }
      oldId = String(prev.id)
    } else {
      oldId = String(left)
      newId = String(right)
    }
    const revOf = (id) => {
      const v = vs.find(x => String(x.id) === String(id))
      return v && v.revision_number != null ? `Rev ${v.revision_number}${v.issue_tag?.label ? ` · ${v.issue_tag.label}` : ""}` : ""
    }
    const file = this._filesData?.[String(this._compareSourceId)] || {}
    const base = [file.sheet_number, file.title].filter(Boolean).join(" — ") || file.file_name || "Sheet"
    const title = `${base} · Overlay: ${revOf(oldId) || "older"} (red) vs ${revOf(newId) || "newer"} (blue)`

    this.closeComparePicker()
    const viewer = this.element.querySelector("[data-controller='action-item-pdf-viewer']")
    const viewerCtrl = viewer && this.application.getControllerForElementAndIdentifier(viewer, "action-item-pdf-viewer")
    if (!viewerCtrl || typeof viewerCtrl.openOverlayCompare !== "function") return
    const urlFor = (vid) => this.pdfViewerBaseUrlValue.replace(/\/0\//, `/${vid}/`)
    viewerCtrl.openOverlayCompare({ oldUrl: urlFor(oldId), newUrl: urlFor(newId), title })
  }

  // Order two versions of the same sheet so the OLDER one lands on the left.
  // sheet_history is newest-first, so a higher array index = older.
  _orderVersions(aId, bId) {
    const vs = this._compareVersions || []
    const idx = (vid) => vs.findIndex(v => String(v.id) === String(vid))
    return idx(aId) > idx(bId) ? [aId, bId] : [bId, aId]
  }

  // Show only the controls that apply to this sheet's version count:
  //   1 version  → nothing to compare (message)
  //   2 versions → "Compare to previous" only
  //   3+ versions→ "Compare to previous" + a list of older versions to pick
  _renderComparePicker(versions) {
    const count = versions.length
    this._setComparePickerSections({
      previous: count >= 2,
      older: count > 2,
      empty: count < 2,
      message: this._comparePickerEmptyDefault
    })

    if (count > 2 && this.hasComparePickerListTarget) {
      const others = versions.filter(v => String(v.id) !== String(this._compareSourceId))
      this.comparePickerListTarget.innerHTML = others.map(v => {
        const rev = this._esc(v.revision_number != null ? `Rev ${v.revision_number}` : "—")
        const date = this._esc(v.formatted_date || "")
        const who = this._esc(v.uploader_name || "")
        const meta = [date, who].filter(Boolean).join(" · ")
        const current = v.is_current ? `<span class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[13px] font-medium bg-emerald-100 text-emerald-700">Current</span>` : ""
        return `<button type="button" data-file-id="${v.id}"
                  data-action="click->drawings-specs#compareWithPicked"
                  class="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left hover:bg-gray-50 cursor-pointer transition-colors">
            <span class="text-[13px] font-semibold text-gray-800 shrink-0 inline-flex items-center gap-1.5">${rev}${issueTagChip(v.issue_tag, v.drawing_set_name)}${current}</span>
            <span class="min-w-0 flex-1">
              ${meta ? `<span class="block text-[13px] text-gray-500 truncate">${meta}</span>` : ""}
            </span>
          </button>`
      }).join("")
    }
  }

  // Toggle the picker's sections. Pass { loading: true } for the brief
  // history fetch, or explicit { previous, older, empty } booleans.
  _setComparePickerSections({ loading = false, previous = false, older = false, empty = false, message = null } = {}) {
    if (this.hasComparePickerPreviousWrapTarget) this.comparePickerPreviousWrapTarget.classList.toggle("hidden", loading || !previous)
    if (this.hasComparePickerOlderSectionTarget) this.comparePickerOlderSectionTarget.classList.toggle("hidden", loading || !older)
    if (this.hasComparePickerEmptyTarget) {
      if (loading) this.comparePickerEmptyTarget.textContent = "Loading…"
      else if (message != null) this.comparePickerEmptyTarget.textContent = message
      this.comparePickerEmptyTarget.classList.toggle("hidden", !(loading || empty))
    }
  }

  _esc(s) {
    return String(s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }

  _updateFilterBadge() {
    if (!this.hasFilterBadgeTarget) return
    let count = 0
    if (this._filters.date_from || this._filters.date_to) count++
    if (this._filters.uploader_id) count++
    if (this._drawingSetFilter) count++

    if (count > 0) {
      this.filterBadgeTarget.classList.remove("hidden")
      this.filterBadgeTarget.classList.add("inline-flex")
      this.filterBadgeTarget.textContent = count
      if (this.hasClearFiltersRowTarget) this.clearFiltersRowTarget.style.display = ""
    } else {
      this.filterBadgeTarget.classList.add("hidden")
      this.filterBadgeTarget.classList.remove("inline-flex")
      if (this.hasClearFiltersRowTarget) this.clearFiltersRowTarget.style.display = "none"
    }
  }

  _restoreSavedFilters() {
    const saved = this.savedFiltersValue
    // Per-user expand/collapse memory rides alongside the saved filters.
    this._groupState = (saved && saved._groups) || {}
    if (!saved || Object.keys(saved).length === 0) return

    this._filters = { ...saved }
    delete this._filters._groups

    if (this._filters.search && this.hasSearchInputTarget) {
      this.searchInputTarget.value = this._filters.search
      this._toggleSearchClear()
    }
    if (this._filters.date_from && this.hasDateFromTarget) {
      this.dateFromTarget.value = this._filters.date_from
    }
    if (this._filters.date_to && this.hasDateToTarget) {
      this.dateToTarget.value = this._filters.date_to
    }

    this._updateFilterBadge()
  }

  _persistFilters() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const payload = { ...this._filters }
    if (this._groupState && Object.keys(this._groupState).length) payload._groups = this._groupState
    fetch(this.saveFiltersUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      credentials: "same-origin",
      body: JSON.stringify({ filters: payload })
    })
  }

  // Per-user memory of which combined / version groups the user left open —
  // restored on the next visit so the list keeps their layout.
  _groupExpanded(key, def) {
    return (this._groupState && key in this._groupState) ? this._groupState[key] : def
  }

  _recordGroup(key, expanded) {
    if (!key) return
    ;(this._groupState ||= {})[key] = expanded
    clearTimeout(this._groupSaveTimer)
    this._groupSaveTimer = setTimeout(() => this._persistFilters(), 400)
  }

  _onKeydown(event) {
    if (!this._modalOpen) return
    if (event.key === "Escape") {
      this.closeDetail()
    } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault()
      this.prevFile()
    } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault()
      this.nextFile()
    }
  }

  _escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  // ── Distribution Modal ────────────────────────────────────────────

  async _openDistributeModal(uploadedIds) {
    this._distributeFileIds = uploadedIds
    this._distributeSelectedEmails = new Map() // email -> { name, email }
    this._distributeLockedEmails = new Set() // emails with documents_notif (pre-checked, can't uncheck)
    this._distributeMembers = []

    // Fetch members grouped by company
    try {
      const resp = await fetch(this.distributionMembersUrlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!resp.ok) return
      this._distributeMembers = await resp.json()
    } catch {
      return
    }

    if (this._distributeMembers.length === 0) return

    // Pre-select users with documents_notif enabled
    for (const company of this._distributeMembers) {
      for (const user of company.users) {
        if (user.documents_notif) {
          this._distributeSelectedEmails.set(user.email, { name: user.name, email: user.email })
          this._distributeLockedEmails.add(user.email)
        }
      }
    }

    this.distributeSearchInputTarget.value = ""
    this._renderDistributeList()
    this._updateDistributeUI()
    this.distributeModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  filterDistributeMembers() {
    this._renderDistributeList()
  }

  _renderDistributeList() {
    const query = (this.distributeSearchInputTarget.value || "").toLowerCase().trim()
    let html = ""

    for (const company of this._distributeMembers) {
      const users = company.users.filter(u => {
        if (!query) return true
        return u.name.toLowerCase().includes(query) || u.email.toLowerCase().includes(query)
      })
      if (users.length === 0) continue

      html += `<div class="px-5 pt-3 pb-1">
        <span class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">${this._escapeHtml(company.name)}</span>
      </div>`

      for (const user of users) {
        const checked = this._distributeSelectedEmails.has(user.email)
        const locked = this._distributeLockedEmails.has(user.email)
        const disabledAttr = locked ? "disabled" : ""
        const cursorClass = locked ? "cursor-default" : "cursor-pointer"
        const labelOpacity = locked ? "opacity-70" : ""

        html += `<label class="flex items-center gap-2.5 px-5 py-1.5 hover:bg-gray-50 ${cursorClass} select-none ${labelOpacity}">
          <input type="checkbox" class="form-checkbox rounded shrink-0" style="accent-color: var(--color-brand); width: 14px; height: 14px"
                 data-email="${this._escapeHtml(user.email)}"
                 data-name="${this._escapeHtml(user.name)}"
                 data-action="change->drawings-specs#toggleDistributeMember"
                 ${checked ? "checked" : ""} ${disabledAttr}>
          <div class="min-w-0 flex-1">
            <div class="text-sm text-gray-800 truncate">${this._escapeHtml(user.name)}</div>
            <div class="text-[12px] text-gray-400 truncate">${this._escapeHtml(user.email)}</div>
          </div>
          ${locked ? '<span class="text-[11px] text-gray-400 shrink-0">auto</span>' : ""}
        </label>`
      }
    }

    if (!html) {
      html = '<div class="px-5 py-4 text-sm text-gray-400 text-center">No members found</div>'
    }

    this.distributeDropdownListTarget.innerHTML = html
  }

  toggleDistributeMember(event) {
    const cb = event.currentTarget
    const email = cb.dataset.email
    const name = cb.dataset.name

    if (cb.checked) {
      this._distributeSelectedEmails.set(email, { name, email })
    } else {
      this._distributeSelectedEmails.delete(email)
    }

    this._updateDistributeUI()
  }

  toggleDistributeSelectAll(event) {
    const checked = event.currentTarget.checked
    for (const company of this._distributeMembers) {
      for (const user of company.users) {
        if (checked) {
          this._distributeSelectedEmails.set(user.email, { name: user.name, email: user.email })
        } else if (!this._distributeLockedEmails.has(user.email)) {
          this._distributeSelectedEmails.delete(user.email)
        }
      }
    }
    this._renderDistributeList()
    this._updateDistributeUI()
  }

  _updateDistributeUI() {
    const count = this._distributeSelectedEmails.size
    const totalUsers = this._distributeMembers.reduce((sum, c) => sum + c.users.length, 0)

    // Update send button
    this.distributeSendBtnTarget.disabled = count === 0

    // Update count label
    this.distributeCountTarget.textContent = count > 0 ? `(${count}/${totalUsers})` : ""

    // Update select-all checkbox state
    if (totalUsers > 0) {
      this.distributeSelectAllTarget.checked = count >= totalUsers
      this.distributeSelectAllTarget.indeterminate = count > 0 && count < totalUsers
    }
  }

  async submitDistribute() {
    const emails = [...this._distributeSelectedEmails.keys()]
    if (emails.length === 0) return

    this.distributeSendBtnTarget.disabled = true
    this.distributeSendBtnTarget.textContent = "Sending..."

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(this.distributeUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ emails, ids: this._distributeFileIds })
      })

      if (resp.ok) {
        this._closeDistributeModal()
        this._showToast("Notification sent successfully")
      } else {
        this._showToast("Failed to send notification", "error")
      }
    } catch {
      this._showToast("Failed to send notification", "error")
    } finally {
      this.distributeSendBtnTarget.disabled = false
      this.distributeSendBtnTarget.textContent = "Send Notification"
    }
  }

  skipDistribute() {
    this._closeDistributeModal()
  }

  _closeDistributeModal() {
    this.distributeModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    this._distributeFileIds = []
    this._distributeSelectedEmails = new Map()
    this._distributeLockedEmails = new Set()
  }

  _showToast(message, type = "success", dismissAfter = 5000) {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", String(dismissAfter))
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── Bluebeam Studio Sessions ──────────────────────────────────────
  // Open a sheet in Bluebeam (browser or Revu), work on it, then finish
  // the session choosing whether the marked-up file becomes a NEW version
  // or replaces the CURRENT version's file. Mirrors design_step_controller.

  _bluebeamUrl(template, id) {
    return template.replace("__ID__", id)
  }

  _bluebeamEligible(file) {
    return file && file.is_pdf && !file.combined && file.status === "current"
  }

  // Row-menu entries for the file's current Bluebeam state.
  _bluebeamMenuItems(file) {
    if (!this._bluebeamEligible(file) || !this.hasBluebeamOpenUrlTemplateValue) return ""
    if (["creating", "finalizing", "syncing"].includes(file.bluebeam_status)) return ""

    if (file.bluebeam_status === "active") {
      return `
        <button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                data-action="click->drawings-specs#openInBluebeam" data-file-id="${file.id}">Open in Bluebeam</button>
        <button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                data-action="click->drawings-specs#showBluebeamFinishModal" data-file-id="${file.id}">Finish Bluebeam Session</button>
        <button type="button" class="block w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                data-action="click->drawings-specs#showBluebeamDiscardModal" data-file-id="${file.id}">Discard Bluebeam Session</button>`
    }
    return `
      <button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
              data-action="click->drawings-specs#openInBluebeam" data-file-id="${file.id}">Open in Bluebeam</button>`
  }

  // Inline status chip rendered next to the title.
  _bluebeamChip(file) {
    if (!this._bluebeamEligible(file)) return ""
    if (file.bluebeam_status === "active") {
      const action = this.bluebeamAllowedValue
        ? `onclick="event.stopPropagation()" data-action="click->drawings-specs#showBluebeamFinishModal" data-file-id="${file.id}" title="Finish the Bluebeam session and save markups"`
        : `onclick="event.stopPropagation()" title="This document has an open Bluebeam session"`
      return `<button type="button" ${action}
        class="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer transition-colors align-middle">
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/></svg>
        In Bluebeam</button>`
    }
    if (["creating", "finalizing", "syncing"].includes(file.bluebeam_status)) {
      const label = file.bluebeam_status === "creating" ? "Opening in Bluebeam..." : "Saving from Bluebeam..."
      return `<span class="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium bg-blue-50 text-blue-500 align-middle">
        <svg class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
        ${label}</span>`
    }
    return ""
  }

  openInBluebeam(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.fileId
    if (!id) return
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))
    this._triggerBluebeamOpen(id)
  }

  async _triggerBluebeamOpen(id) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const resp = await fetch(this._bluebeamUrl(this.bluebeamOpenUrlTemplateValue, id), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      const data = await resp.json()

      if (data.needs_auth) {
        window.location.href = data.auth_url
        return
      }
      if (data.status === "active" && data.session_url) {
        this._bluebeamSessionUrl = data.session_url
        this._bluebeamSessionId = data.session_id
        this._setBluebeamRowStatus(id, "active")
        this._showBluebeamPopover()
        return
      }
      if (data.status === "creating") {
        this._setBluebeamRowStatus(id, "creating")
        this._pollBluebeamStatus(id)
      }
      if (data.error === "document_is_final") {
        this._showToast(data.message || "Document is final — unlock first", "error")
      }
    } catch (e) {
      console.error("[drawings-specs] Bluebeam open error:", e)
      this._showToast("Failed to open in Bluebeam. Please try again.", "error")
    }
  }

  _showBluebeamPopover() {
    // Surface the Studio Session ID as a manual-join fallback: if the one-click
    // studio:// hand-off doesn't pull the file into Revu Desktop, the user can
    // paste this ID into Revu (Studio → Join) and the document loads.
    if (this.hasBluebeamSessionIdTarget) {
      this.bluebeamSessionIdTarget.textContent = this._bluebeamSessionId || "—"
    }
    if (this.hasBluebeamSessionIdCopyLabelTarget) {
      this.bluebeamSessionIdCopyLabelTarget.textContent = "Copy"
    }
    if (this.hasBluebeamPopoverTarget) this.bluebeamPopoverTarget.classList.remove("hidden")
    // Larry lives in desktop Revu (2026-08-25) — once someone has picked it,
    // fire Bluebeam's hand-off right away. The popover stays open behind it
    // as the fallback (manual Join ID, browser option).
    let pref = null
    try { pref = localStorage.getItem("conslog-bluebeam-launch") } catch (_) {}
    if (pref === "desktop" && this._bluebeamJoinUrl()) {
      window.open(this._bluebeamJoinUrl(), "_blank")
    }
  }

  // Bluebeam's own session launcher — the documented hand-off, which routes
  // to installed Revu (or explains itself when Revu is missing). The old
  // `studio://studio.bluebeam.com/sessions/…` scheme was invented: on Luma's
  // machine (2026-08-28) it fronted Revu but never loaded the session,
  // because Revu doesn't understand that path. Studio IDs are shown
  // dash-formatted (269-728-452), so a bare 9-digit id gets its dashes back.
  _bluebeamJoinUrl() {
    if (!this._bluebeamSessionId) return null
    const raw = String(this._bluebeamSessionId).trim()
    const id = /^\d{9}$/.test(raw) ? `${raw.slice(0, 3)}-${raw.slice(3, 6)}-${raw.slice(6)}` : raw
    return `https://studio.bluebeam.com/join.html?ID=${encodeURIComponent(id)}`
  }

  copyBluebeamSessionId() {
    if (!this._bluebeamSessionId) return
    navigator.clipboard.writeText(this._bluebeamSessionId).then(() => {
      if (this.hasBluebeamSessionIdCopyLabelTarget) {
        this.bluebeamSessionIdCopyLabelTarget.textContent = "Copied!"
        setTimeout(() => {
          if (this.hasBluebeamSessionIdCopyLabelTarget) this.bluebeamSessionIdCopyLabelTarget.textContent = "Copy"
        }, 2000)
      }
    })
  }

  closeBluebeamPopover() {
    if (this.hasBluebeamPopoverTarget) this.bluebeamPopoverTarget.classList.add("hidden")
  }

  launchBluebeamWeb() {
    try { localStorage.setItem("conslog-bluebeam-launch", "web") } catch (_) {}
    if (this._bluebeamSessionUrl) window.open(this._bluebeamSessionUrl, "_blank")
    this.closeBluebeamPopover()
  }

  launchBluebeamDesktop() {
    try { localStorage.setItem("conslog-bluebeam-launch", "desktop") } catch (_) {}
    const url = this._bluebeamJoinUrl()
    if (url) window.open(url, "_blank")
    this.closeBluebeamPopover()
  }

  copyBluebeamLink() {
    if (this._bluebeamSessionUrl) {
      navigator.clipboard.writeText(this._bluebeamSessionUrl).then(() => {
        if (this.hasBluebeamCopyLinkLabelTarget) {
          this.bluebeamCopyLinkLabelTarget.textContent = "Copied!"
          setTimeout(() => { this.bluebeamCopyLinkLabelTarget.textContent = "Copy Session Link" }, 2000)
        }
      })
    }
  }

  // Finish session — the user decides: new version or save into current.
  showBluebeamFinishModal(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.fileId
    if (!id) return
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))

    this._bluebeamFinishId = id
    const file = this._filesData[String(id)]
    const currentRev = file?.revision_number ? `v${file.revision_number}` : "the current version"
    const nextRev = file?.revision_number ? `v${parseInt(file.revision_number, 10) + 1}` : "a new version"
    if (this.hasBluebeamFinishNextRevTarget) this.bluebeamFinishNextRevTarget.textContent = nextRev
    if (this.hasBluebeamFinishCurrentRevTarget) this.bluebeamFinishCurrentRevTarget.textContent = currentRev

    // Default back to "new version" each time
    this.bluebeamVersionRadioTargets.forEach(r => { r.checked = r.value === "new_version" })
    if (this.hasBluebeamFinishModalTarget) this.bluebeamFinishModalTarget.classList.remove("hidden")
  }

  closeBluebeamFinishModal() {
    if (this.hasBluebeamFinishModalTarget) this.bluebeamFinishModalTarget.classList.add("hidden")
    this._bluebeamFinishId = null
  }

  async confirmBluebeamFinish() {
    const id = this._bluebeamFinishId
    if (!id) return
    const mode = this.bluebeamVersionRadioTargets.find(r => r.checked)?.value || "new_version"
    this.closeBluebeamFinishModal()

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const resp = await fetch(this._bluebeamUrl(this.bluebeamFinishUrlTemplateValue, id), {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin",
        body: JSON.stringify({ version_mode: mode })
      })
      const data = await resp.json()
      if (data.status === "syncing") {
        this._bluebeamFinishMode = mode
        this._setBluebeamRowStatus(id, "syncing")
        this._pollBluebeamStatus(id)
      } else if (data.error) {
        this._showToast(data.error, "error")
      }
    } catch (e) {
      console.error("[drawings-specs] Bluebeam finish error:", e)
      this._showToast("Failed to save markups. Please try again.", "error")
    }
  }

  showBluebeamDiscardModal(event) {
    event.stopPropagation()
    const id = event.currentTarget.dataset.fileId
    if (!id) return
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))
    this._bluebeamDiscardId = id
    if (this.hasBluebeamDiscardModalTarget) this.bluebeamDiscardModalTarget.classList.remove("hidden")
  }

  closeBluebeamDiscardModal() {
    if (this.hasBluebeamDiscardModalTarget) this.bluebeamDiscardModalTarget.classList.add("hidden")
    this._bluebeamDiscardId = null
  }

  async confirmBluebeamDiscard() {
    const id = this._bluebeamDiscardId
    if (!id) return
    this.closeBluebeamDiscardModal()

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      await fetch(this._bluebeamUrl(this.bluebeamCloseUrlTemplateValue, id), {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        credentials: "same-origin"
      })
      this._setBluebeamRowStatus(id, null)
      this._showToast("Bluebeam session discarded")
    } catch (e) {
      console.error("[drawings-specs] Bluebeam discard error:", e)
      this._showToast("Failed to discard session", "error")
    }
  }

  _setBluebeamRowStatus(id, status) {
    const file = this._filesData[String(id)]
    if (!file) return
    file.bluebeam_status = status
    if (this.currentViewValue === "list") this._reRenderTable()
  }

  _pollBluebeamStatus(id) {
    if (this._bluebeamPollers[id]) clearInterval(this._bluebeamPollers[id])

    const url = this._bluebeamUrl(this.bluebeamStatusUrlTemplateValue, id)
    let pollCount = 0
    const maxPolls = 40 // 40 x 3s = 2 minutes
    const stop = () => { clearInterval(this._bluebeamPollers[id]); delete this._bluebeamPollers[id] }

    this._bluebeamPollers[id] = setInterval(async () => {
      pollCount++
      try {
        const resp = await fetch(url, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
        const data = await resp.json()

        if (data.status === "active" && data.session_url) {
          stop()
          this._bluebeamSessionUrl = data.session_url
          this._bluebeamSessionId = data.session_id
          this._setBluebeamRowStatus(id, "active")
          this._showBluebeamPopover()
        } else if (data.status === "closed") {
          stop()
          const mode = this._bluebeamFinishMode || data.version_mode
          if (mode === "current_version") {
            this._showToast("Bluebeam markups saved to the current version")
          } else if (mode === "new_version") {
            this._showToast("Bluebeam markups saved — new version created")
          }
          this._bluebeamFinishMode = null
          this._resetAndLoad()
        } else if (data.status === "no_changes") {
          stop()
          this._bluebeamFinishMode = null
          this._setBluebeamRowStatus(id, null)
          this._showToast("Bluebeam sent the sheet back with no markups on it, so nothing was saved. In Revu, wait until your markups show as uploaded to Studio, then finish the session again.", "error")
        } else if (data.status === "error") {
          stop()
          this._setBluebeamRowStatus(id, null)
          // A reason from the server (e.g. no Bluebeam licence) is long: leave it up to read.
          this._showToast(data.message || "Bluebeam session failed. Please try again.", "error", data.message ? 15000 : 5000)
        } else if (pollCount >= maxPolls) {
          stop()
          this._showToast("Bluebeam is taking longer than expected — check back shortly.", "error")
        }
        // "none"/"creating"/"finalizing"/"syncing" — keep polling
      } catch (e) {
        console.error("[drawings-specs] Bluebeam poll error:", e)
        stop()
      }
    }, 3000)
  }

  // ── Private: File Type Icons ──────────────────────────────────────

  _fileTypeIcon(ext, sizeClass = "w-5 h-5") {
    ext = (ext || "").toLowerCase()

    if (ext === "pdf") {
      return `<svg class="${sizeClass} text-red-500" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clip-rule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z"/></svg>`
    }
    if (["jpg","jpeg","gif","png","tiff","bmp","webp","heic","heif"].includes(ext)) {
      return `<svg class="${sizeClass} text-emerald-500" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.689a1.5 1.5 0 0 0-2.12 0l-.88.879.97.97a.75.75 0 1 1-1.06 1.06l-5.16-5.159a1.5 1.5 0 0 0-2.12 0L3 16.061Zm10.125-7.81a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Z" clip-rule="evenodd"/></svg>`
    }
    if (["xls","xlsx","xlsm"].includes(ext)) {
      return `<svg class="${sizeClass} text-green-600" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clip-rule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z"/></svg>`
    }
    if (["doc","docx","txt"].includes(ext)) {
      return `<svg class="${sizeClass} text-blue-500" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clip-rule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z"/></svg>`
    }
    if (ext === "dwg") {
      return `<svg class="${sizeClass} text-purple-500" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clip-rule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z"/></svg>`
    }
    if (["zip","rar"].includes(ext)) {
      return `<svg class="${sizeClass} text-yellow-600" viewBox="0 0 24 24" fill="currentColor"><path d="M19.5 21a3 3 0 0 0 3-3v-4.5a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h15ZM1.5 10.146V6a3 3 0 0 1 3-3h5.379a2.25 2.25 0 0 1 1.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 0 1 3 3v1.146A4.483 4.483 0 0 0 19.5 9h-15a4.483 4.483 0 0 0-3 1.146Z"/></svg>`
    }
    if (["ppt","pptx"].includes(ext)) {
      return `<svg class="${sizeClass} text-orange-500" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clip-rule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z"/></svg>`
    }
    // Generic
    return `<svg class="${sizeClass} text-gray-400" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5H12a.75.75 0 0 0 0-1.5H8.25Z" clip-rule="evenodd"/><path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z"/></svg>`
  }

  // Purple pulse on a row an AI answer cited: scroll it into view and hold the
  // mark long enough to be seen (Phelipe 2026-08-21). Purple is the AI colour
  // everywhere else in the app, so it reads as "the assistant pointed here".
  _markCitedRow(fileId) {
    const paint = () => {
      const row = this.element.querySelector(`tr[data-file-id="${fileId}"], [data-card-file-id="${fileId}"]`)
      if (!row) return false
      row.scrollIntoView({ behavior: "smooth", block: "center" })
      row.classList.add("ring-2", "ring-purple-500", "ring-inset", "bg-purple-50")
      setTimeout(() => row.classList.remove("ring-2", "ring-purple-500", "ring-inset", "bg-purple-50"), 6000)
      return true
    }
    // The row may still be rendering when the deep link lands.
    if (!paint()) setTimeout(paint, 400)
  }

}
